// src/pages/RequestDetail.js
// FULL FILE — Updated to support NEW lookup wrapper shape:
// { requestId, timing, response: { ... } }
//
// Changes made:
// 1) Unwrap lookup payload via unwrapLookup() and pass only inner response into mapHingeToDetail()
// 2) mapHingeToDetail updated to read the new field names (dtvAnchorId, personType, etc.)
// 3) Keeps existing DETAILS_URL (npw_id) behavior unchanged
//
// NEW CHANGE (THIS REQUEST):
// - If Pending with CP Delegate, "Send Reminder" calls CP Delegate LIST API,
//   collects all primaryEmail values, and sends reminder email to ALL.
// - If Pending with Employee Sponsor, keep current reminder behavior (send to sponsor only).

import { useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "../layout/AppShell";
import "./Approvals.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ENV,
  tbGetJson,
  tbPostJson,
  newReqId as clientNewReqId,
} from "../api/client";
import { formatDateMMDDYYYY } from "../utils/dateFormat";
import {
  getCpErrorMessages,
  hasApiValidationFailure as hasCpValidationFailure,
} from "../utils/cpValidationErrors";
import { validateSecurityIdMmdd8 } from "../utils/securityId";
import ReactivateAttuidModal from "../components/ReactivateAttuidModal";
import OffboardWorkerModal from "../components/OffboardWorkerModal";

/* ===== ENV endpoints ===== */
const DETAILS_URL = ENV.APPROVAL_DETAILS_URL;
const APPROVAL_ACTION_URL = ENV.APPROVAL_ACTION_URL;
const OFFBOARD_URL = (ENV.OFFBOARD_URL || "").trim();
const EMAIL_NOTIFY_URL = ENV.NPW_EMAIL_NOTIFICATIONS_URL || ENV.REMINDER_URL;
const TB_BASE = (ENV.TB_BASE || "").replace(/\/+$/, "");
const ONBOARDING_NEW_URL =
  ENV.ONBOARDING_NEW_URL ||
  (TB_BASE && `${TB_BASE}/api/npw-onboarding-new`) ||
  "";
const CP_VALIDATE_URL =
  ENV.CP_VALIDATE_URL ||
  ENV.CINGLEPOINT_URL ||
  ENV.NPW_CP_VALIDATE_URL ||
  ENV.ONBOARDING_CP_VALIDATE_URL ||
  (TB_BASE && `${TB_BASE}/api/v1/cinglepoint`) ||
  "" ||
  "";

// Role catalog (Hinge)
const HINGE_ROLE_LIST_URL = (
  ENV.HINGE_ROLE_LIST_URL ||
  "https://teambridge.test.directv.com/dtvcs-teambridge-api/api/v1/npw-hinge-rolecatalog"
).replace(/\/+$/, "");

const TB_HINGE_SEARCH_URL = (
  ENV.TB_HINGE_SEARCH_URL ||
  ENV.TB_HINGE_PERSON_SEARCH_URL ||
  ENV.TB_HINGE_SEARCH_PROXY_URL ||
  ""
).replace(/\/+$/, "");
const HINGE_SSN_MIN_DIGITS = Number(ENV.HINGE_SSN_MIN_DIGITS || 8);
const HINGE_SSN_DEBOUNCE_MS = Number(ENV.HINGE_SSN_DEBOUNCE_MS || 2000);

const HINGE_WEBPHONE_VALIDATE_URL = (
  ENV.HINGE_WEBPHONE_VALIDATE_URL ||
  "https://api-hingedev-directv.com/api/v1/validation"
).replace(/\/+$/, "");
const TB_HINGE_WEBPHONE_VALIDATE_URL = (
  ENV.TB_HINGE_WEBPHONE_VALIDATE_URL ||
  ENV.TB_HINGE_VALIDATE_URL ||
  HINGE_WEBPHONE_VALIDATE_URL ||
  ""
).replace(/\/+$/, "");

/* ===== TB-HINGE proxy endpoints (prefer TB proxy for browser calls) ===== */
const TB_HINGE_PERSON_LOOKUP_URL =
  ENV.TB_HINGE_PERSON_LOOKUP_URL ||
  ENV.TB_HINGE_LOOKUP_URL ||
  ENV.TB_LOOKUP_URL ||
  "";

/* ===== NEW: TB-HINGE list endpoint (for CP Delegate reminder recipients) ===== */
const TB_HINGE_PERSON_LIST_URL =
  ENV.TB_HINGE_PERSON_LIST_URL ||
  ENV.TB_HINGE_LIST_URL ||
  ENV.TB_HINGE_SEARCH_URL ||
  "";

/* ===== Search/Navigation session keys ===== */
const RETURN_PATH_KEY = "npw.search.returnPath";
const FROM_SEARCH_KEY = "npw.search.fromSearch";
const REOPEN_SEARCH_KEY = "npw.search.reopen";
const REACTIVE_ATTUID_VISIBLE_ROLES = [
  "nominator",
  "fte nominator",
  "employee sponsor",
  "employee supplier admin",
  "dtv ops admin",
  "dtvops admin",
  "cp channel delegate",
];
const REACTIVE_ATTUID_STATUS_KEYS = ["inactive", "terminated"];
const INFOREQ_SUBMIT_ALLOWED_ROLES = [
  "nominator",
  "fte nominator",
  "employee sponsor admin",
  "dtv ops admin",
];
const INFOREQ_COMMENT_MAX_LENGTH = 1000;
const INFOREQ_SUCCESS_RELOAD_DELAY_MS = 1200;

/* ===== helpers (kept consistent with ApprovalDetail) ===== */
const show = (v) => (v == null ? "" : String(v));
const truthy = (v) =>
  v === true || v === 1 || v === "1" || v === "true" || v === "t" || v === "T";
// Gate CP Delegate reminder emails; disabled by default unless explicitly enabled.
const CP_EMAIL_ALLOWED = truthy(ENV.CP_EMAIL_ALLOWED);
const IS_REACTIVATE_ATTUID_NEEDED =
  ENV.IS_REACTIVATE_ATTUID_NEEDED === undefined ||
  ENV.IS_REACTIVATE_ATTUID_NEEDED === null ||
  ENV.IS_REACTIVATE_ATTUID_NEEDED === ""
    ? true
    : truthy(ENV.IS_REACTIVATE_ATTUID_NEEDED);
const IS_VALIDATE_CP_NEEDED =
  ENV.IS_VALIDATE_CP_NEEDED === undefined ||
  ENV.IS_VALIDATE_CP_NEEDED === null ||
  ENV.IS_VALIDATE_CP_NEEDED === ""
    ? true
    : truthy(ENV.IS_VALIDATE_CP_NEEDED);

const isUSCountry = (v) => {
  const s = String(v ?? "")
    .trim()
    .toLowerCase();
  return (
    s === "us" ||
    s === "usa" ||
    s === "united states" ||
    s === "united states of america" ||
    s.includes("united states")
  );
};

const maskID = (v) => {
  const s = String(v ?? "");
  if (!s) return "";
  if (s.length <= 4) return "*".repeat(Math.max(0, 8 - s.length)) + s;
  return s.length <= 4
    ? "*".repeat(s.length)
    : "*".repeat(s.length - 4) + s.slice(-4);
};

const isFgSubmitted = (name) =>
  String(name || "")
    .trim()
    .toLowerCase() === "fg submitted";

function extractOffboardError(err) {
  if (!err) return "Offboard failed.";
  const msg = err?.message || "";
  return String(msg || "Offboard failed.");
}

const firstNonEmpty = (...vals) => {
  for (const v of vals) {
    const s = String(v ?? "").trim();
    if (s && s.toLowerCase() !== "null" && s.toLowerCase() !== "undefined")
      return s;
  }
  return "";
};

function encodeLoggedInUserDtvId(loginDtvIdRaw) {
  const dtvId = String(loginDtvIdRaw || "").trim();
  if (!dtvId) return "";
  return btoa(`${dtvId}_*_true`);
}

const validateRoleCatalogMatch = (lookup, roleCatalogResponse) => {
  const normalizeId = (value) =>
    String(value ?? "")
      .trim()
      .toLowerCase();

  const lookupRoleId = normalizeId(lookup?.cinglepointRoleId);

  const lookupProfileId = normalizeId(lookup?.cinglepointProfileId);

  console.log(
    "[ROLE CATALOG] FULL OBJECT:",
    JSON.stringify(roleCatalogResponse, null, 2),
  );

  const roles = Array.isArray(roleCatalogResponse)
    ? roleCatalogResponse
    : Array.isArray(roleCatalogResponse?.response?.roles)
      ? roleCatalogResponse.response.roles
      : Array.isArray(roleCatalogResponse?.roles)
        ? roleCatalogResponse.roles
        : [];

  console.log("[ROLE CATALOG] lookupRoleId:", lookupRoleId);
  console.log("[ROLE CATALOG] lookupProfileId:", lookupProfileId);
  console.log("[ROLE CATALOG] roles:", roles);
  console.log("[ROLE CATALOG] roles.length:", roles.length);

  if (!roles.length) {
    console.error("[ROLE CATALOG] No roles found in API response");
    return false;
  }

  for (const role of roles) {
    const cpRoles = Array.isArray(role?.cinglepointRoles)
      ? role.cinglepointRoles
      : [];

    console.log("[ROLE CATALOG] cinglepointRoles:", cpRoles);

    for (const cpRole of cpRoles) {
      const cpRoleId = normalizeId(cpRole?.cpRoleId);

      console.log("[ROLE CATALOG] ROLE COMPARISON:", {
        lookupRoleId,
        cpRoleId,
        match: lookupRoleId === cpRoleId,
      });

      if (lookupRoleId && cpRoleId && lookupRoleId === cpRoleId) {
        console.log(
          "%c[ROLE CATALOG] ROLE MATCH FOUND = TRUE",
          "color: green; font-weight: bold;",
        );

        return true;
      }

      const cpProfiles = Array.isArray(cpRole?.cpProfiles)
        ? cpRole.cpProfiles
        : [];

      for (const profile of cpProfiles) {
        const cpProfileId = normalizeId(profile?.cpProfileId);

        console.log("[ROLE CATALOG] PROFILE COMPARISON:", {
          lookupProfileId,
          cpProfileId,
          match: lookupProfileId === cpProfileId,
        });

        if (lookupProfileId && cpProfileId && lookupProfileId === cpProfileId) {
          console.log(
            "%c[ROLE CATALOG] PROFILE MATCH FOUND = TRUE",
            "color: green; font-weight: bold;",
          );

          return true;
        }
      }
    }
  }

  console.error(
    "%c[ROLE CATALOG] NO MATCH FOUND",
    "color: red; font-weight: bold;",
    {
      lookupRoleId,
      lookupProfileId,
    },
  );

  return false;
};

const collectApiMessages = (value, out = [], depth = 0) => {
  if (depth > 5 || value == null) return out;
  if (typeof value === "string") {
    const msg = value.trim();
    if (msg) out.push(msg);
    return out;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectApiMessages(item, out, depth + 1));
    return out;
  }
  if (typeof value === "object") {
    const keys = [
      "message",
      "error",
      "errorMessage",
      "detail",
      "reason",
      "errors",
      "validationErrors",
      "fieldErrors",
      "results",
      "body",
      "agentBulkResults",
      "messages",
    ];
    keys.forEach((k) => collectApiMessages(value?.[k], out, depth + 1));
  }
  return out;
};

const hasApiValidationFailure = (resp) => {
  return hasCpValidationFailure(resp);
};

const mapHingeToDetail = mapLookupToDetail;

/* Only show row if value is real (not null/empty/"null"/"undefined") */
const hasVal = (v) => {
  const s = String(v ?? "").trim();
  return (
    s !== "" && s.toLowerCase() !== "null" && s.toLowerCase() !== "undefined"
  );
};
const hv = hasVal;

const statusPillClass = (value) => {
  const key = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, "");
  if (key === "approved") return "pill-ok";
  if (key === "rejected") return "pill-bad";
  if (key === "inforequested") return "pill-warm";
  return "pill-blue";
};

const shouldShowAttuidSupervisor = (statusValue, supervisorValue) => {
  const key = String(statusValue || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, "");
  return key === "active" && hasVal(supervisorValue);
};

// Normalizes ids/emails for safe equality checks (case-insensitive, trimmed)
const normId = (v) =>
  String(v ?? "")
    .trim()
    .toLowerCase();

/* Local render: YYYY-MM-DD HH:mm:ss in local time */
const toDate = (s) => (s ? new Date(s) : null);

function makeCorrelationId() {
  try {
    if (typeof clientNewReqId === "function") {
      const v = clientNewReqId();
      if (v) return String(v);
    }
  } catch {}
  try {
    if (typeof window !== "undefined" && window.tb?.newReqId) {
      const maybe = window.tb.newReqId();
      if (maybe) return String(maybe);
    }
  } catch {}
  try {
    if (typeof window !== "undefined" && window.crypto?.randomUUID)
      return window.crypto.randomUUID();
  } catch {}
  return `req_${Date.now()}`;
}

function fmtDateLocal(s) {
  const d = toDate(s);
  if (!d) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}:${pad(d.getSeconds())}`;
}

/* ===== lane visuals (exactly like ApprovalDetail) ===== */
const pill = ({ approved, rejected, sendback }) =>
  approved
    ? { cls: "pill pill-ok", txt: "Approved" }
    : rejected
      ? { cls: "pill pill-bad", txt: "Rejected" }
      : sendback
        ? { cls: "pill pill-warm", txt: "Info Requested" }
        : { cls: "pill pill-blue", txt: "Pending" };

const tint = ({ approved, rejected, sendback }) =>
  approved
    ? {
        backgroundColor: "rgba(22,163,74,.08)",
        boxShadow: "inset 0 0 0 1px rgba(22,163,74,.16)",
      }
    : rejected
      ? {
          backgroundColor: "rgba(220,38,38,.08)",
          boxShadow: "inset 0 0 0 1px rgba(220,38,38,.16)",
        }
      : sendback
        ? {
            backgroundColor: "rgba(217,119,6,.10)",
            boxShadow: "inset 0 0 0 1px rgba(217,119,6,.18)",
          }
        : {};

/* ===== comments helpers (display only) — match ApprovalDetail ===== */
const isoNow = () => new Date().toISOString().replace(/\.\d{3}Z$/, "Z");

function normalizeIsoLine(line = "") {
  const m = String(line || "").match(
    /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})(?:\.\d{1,3})?Z$/,
  );
  if (!m) return line;
  return `${m[1]} ${m[2]}`;
}
function cleanCommentText(t = "") {
  // Normalize ISO lines into local-friendly format like ApprovalDetail does.
  // Note: we keep it as "YYYY-MM-DD HH:mm:ss" (still UTC content) but visually consistent.
  return String(t || "")
    .split("\n")
    .map((ln) => normalizeIsoLine(ln))
    .join("\n")
    .trim();
}
function splitBlocks(t = "") {
  return String(t || "")
    .split(/\n\s*\n/g)
    .map((x) => x.trim())
    .filter(Boolean)
    .map((text) => ({ text }));
}

function normalizeHingePeople(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (typeof payload === "string") {
    try {
      return normalizeHingePeople(JSON.parse(payload));
    } catch {
      return [];
    }
  }
  const wrapped =
    payload?.response && typeof payload.response === "object"
      ? payload.response
      : null;
  const p = wrapped || payload;
  return (
    p.personDetails ||
    p.personList ||
    p.personDetailsList ||
    p.data ||
    p.results ||
    p.items ||
    []
  );
}

function hingePersonFullName(p) {
  const first = String(p?.firstName ?? p?.first_name ?? "").trim();
  const last = String(p?.lastName ?? p?.last_name ?? "").trim();
  const full = `${first} ${last}`.trim();
  return full || "";
}

// Synth comment: when sponsor approved AND explicitly chose ATT UID Request = No (onboarding only)
function attChoiceBlock(d, sponsorApproved) {
  if (truthy(d?.offboard_by_dtv_id) || truthy(d?.offboard_by_attuid))
    return null;
  if (!sponsorApproved) return null;
  if (d?.employee_sponsor_attuid_requested !== false) return null;

  const who = (
    d?.dtv_approver_name ||
    d?.approver_name ||
    d?.approved_by ||
    d?.submitter_name ||
    d?.requested_by ||
    "Approver"
  ).trim();
  const when = (
    d?.dtv_approver_datetime ||
    d?.approved_on ||
    d?.requested_on ||
    isoNow()
  ).trim();

  const lines = [who, when, "ATT UID Request: No"];
  const note = String(d?.employee_sponsor_attuid_request_comment || "").trim();
  if (note) lines.push(note);

  return { text: lines.join("\n"), source: "DTV" };
}

function collectComments(d, sponsorApproved) {
  const list = [];

  // Some flows (notably lookup mapping) initialize these arrays as empty.
  // If empty, we still want to fall back to the string-based comment fields.
  const hasDtvArr =
    Array.isArray(d?.approver_comments) && d.approver_comments.length > 0;
  const hasAttArr =
    Array.isArray(d?.attuid_approver_comments) &&
    d.attuid_approver_comments.length > 0;

  // Newer shape arrays (already flat objects)
  if (hasDtvArr) {
    d.approver_comments.forEach((c) => {
      if (c?.text || c?.by || c?.on)
        list.push({ text: c.text || "", source: "DTV" });
    });
  }
  if (hasAttArr) {
    d.attuid_approver_comments.forEach((c) => {
      if (c?.text || c?.by || c?.on)
        list.push({ text: c.text || "", source: "ATTUID" });
    });
  }

  // Older / string fields
  if (!hasDtvArr) {
    if (d?.dtv_approve_comments)
      splitBlocks(d.dtv_approve_comments).forEach((c) =>
        list.push({ ...c, source: "DTV" }),
      );
    if (d?.dtv_rejection_comments)
      splitBlocks(d.dtv_rejection_comments).forEach((c) =>
        list.push({ ...c, source: "DTV" }),
      );
    if (d?.dtv_sendback_comments)
      splitBlocks(d.dtv_sendback_comments).forEach((c) =>
        list.push({ ...c, source: "DTV" }),
      );

    // Unified fields (your newer payload)
    if (d?.approve_comments)
      splitBlocks(d.approve_comments).forEach((c) =>
        list.push({ ...c, source: "DTV" }),
      );
    if (d?.rejection_comments)
      splitBlocks(d.rejection_comments).forEach((c) =>
        list.push({ ...c, source: "DTV" }),
      );
    if (d?.sendback_comments)
      splitBlocks(d.sendback_comments).forEach((c) =>
        list.push({ ...c, source: "DTV" }),
      );
  }

  if (!hasAttArr) {
    if (d?.attuid_approve_comments)
      splitBlocks(d.attuid_approve_comments).forEach((c) =>
        list.push({ ...c, source: "ATTUID" }),
      );
    if (d?.attuid_rejection_comments)
      splitBlocks(d.attuid_rejection_comments).forEach((c) =>
        list.push({ ...c, source: "ATTUID" }),
      );
    if (d?.attuid_sendback_comments)
      splitBlocks(d.attuid_sendback_comments).forEach((c) =>
        list.push({ ...c, source: "ATTUID" }),
      );
  }

  const attBlock = attChoiceBlock(d, sponsorApproved);
  if (attBlock) list.push(attBlock);

  const seen = new Set();
  return list.filter((c) => {
    const key = `${c.source}|${String(c.text || "").trim()}`;
    if (!String(c.text || "").trim()) return false;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
/* ==========================================================
   NEW: unwrap lookup payload
   - Accepts either wrapper or already-flat object
   ========================================================== */
function unwrapLookup(payload) {
  if (!payload || typeof payload !== "object") return null;
  if (payload.response && typeof payload.response === "object")
    return payload.response;
  return payload;
}

/* ==========================================================
   Approval Details mapping (NPWID path)
   - Keep payload as-is, but provides a single place to normalize/alias if backend evolves.
   ========================================================== */
function mapApprovalDetailsToDetail(d) {
  if (!d || typeof d !== "object") return {};
  return {
    ...d,
    dtvidStatus: d?.status ?? d?.personStatus ?? d?.person_status ?? null,
    attuidStatus: d?.attuidStatus ?? d?.attuid_status ?? null,
    attuidDeactivatedDate:
      d?.attuidDeactivatedDate ?? d?.attuid_deactivated_date ?? null,
    attuidSupervisor: d?.attuidSupervisor ?? d?.attuid_supervisor ?? null,
  };
}

/* ===== HINGE (optional) mapping ===== */
function mapLookupToDetail(h) {
  if (!h) return {};

  // sponsor approval in lookup response is approverName/approvalDate
  const sponsorApproved =
    hv(h?.approverDtvId) && hv(h?.approverName) && hv(h?.approvalDate);

  // CP approval in lookup response is attuidApproverName/attuidApprovalDate
  const cpApproved =
    hv(h?.attuidApproverDtvId) ||
    hv(h?.attuidApproverName) ||
    hv(h?.attuidApprovalDate);

  // ATTUID Required flag (from Hinge lookup)
  const rawAttReq = truthy(
    h?.isAttuidRequested ??
      h?.attuidRequired ??
      h?.attuid_required ??
      h?.isATTUIDRequested ??
      h?.attUidRequested,
  );

  // Role exception: apply the new cinglepointRoleId/cinglepointProfileId rule
  // for ALL roles except FTE Nominator.
  let isFteNominatorRole = false;
  try {
    const r = String(sessionStorage.getItem("tb_role") || "")
      .toLowerCase()
      .replace(/[\s_-]+/g, " ")
      .trim();
    isFteNominatorRole = r === "fte nominator";
  } catch {
    /* ignore */
  }

  const statusLc = String(h?.personStatus || "")
    .trim()
    .toLowerCase();
  const isExistingLookupStatus =
    /(^|\b)(active|inactive|terminated)(\b|$)/.test(statusLc);

  const spRoleId =
    (hv(h?.cinglepointRoleId) ? String(h.cinglepointRoleId).trim() : "") ||
    (hv(h?.singlepointRoleId) ? String(h.singlepointRoleId).trim() : "");
  const spProfileId =
    (hv(h?.cinglepointProfileId)
      ? String(h.cinglepointProfileId).trim()
      : "") ||
    (hv(h?.singlepointProfileId) ? String(h.singlepointProfileId).trim() : "");
  const attReqFromSinglePoint = Boolean(spRoleId || spProfileId);

  const personTypeRaw = String(h?.personType ?? "").trim();
  // Per requirement: ONLY when personType is exactly Employee or Regular (lookup flow),
  // force CP-only rail. Otherwise, keep existing routing logic unchanged.
  const personTypeNorm = personTypeRaw.toLowerCase().trim();
  const forceCpOnly =
    personTypeNorm === "employee" || personTypeNorm === "regular";

  const isNonContractor =
    hv(personTypeRaw) && personTypeNorm !== "non employee-non sales contractor";

  const cpInfoPresent = cpApproved;

  const attReq =
    !isFteNominatorRole && isExistingLookupStatus
      ? attReqFromSinglePoint
      : rawAttReq || isNonContractor || cpInfoPresent;

  // FTE Nominated (tolerant mapping). Leave undefined if unknown.
  let fteNom;
  const rawFte =
    h?.fte_nominated ??
    h?.fteNominated ??
    h?.isFteNominated ??
    h?.is_fte_nominated ??
    h?.fteRequested ??
    h?.fte_requested;
  if (rawFte !== undefined && rawFte !== null && String(rawFte).trim() !== "") {
    fteNom = truthy(rawFte);
  } else {
    const pt = String(h?.personType ?? h?.person_type ?? "").toLowerCase();
    if (pt.includes("fte")) fteNom = true;
  }

  // Location fields are not present in your new response; keep tolerant mapping
  const hingeState =
    h.state ||
    h.workState ||
    h.homeState ||
    h.stateName ||
    h.locationState ||
    "";
  const hingeCity =
    h.city || h.workCity || h.homeCity || h.cityName || h.locationCity || "";
  const hingeCountry =
    h.countryOfWorking ||
    h.country ||
    h.countryName ||
    h.locationCountry ||
    h.countryOfCitizenship ||
    "";

  // Normalize booleans/strings
  const nonBillable =
    h.nonBillableIndicator ?? h.nonBillable ?? h.non_billable ?? "";
  const occupancy =
    h.occupancyIndicator ?? h.occupancy ?? h.occupancy_indicator ?? "";

  // ATT Role CP Role: prefer explicit CP role fields; fallback to cinglepointRoleId when CP role is null/blank.
  const cpRoleIdFromHinge =
    (hv(h?.attRoleCpRoleId) ? String(h.attRoleCpRoleId).trim() : "") ||
    (hv(h?.att_role_cp_role_id) ? String(h.att_role_cp_role_id).trim() : "") ||
    (hv(h?.att_cp_role) ? String(h.att_cp_role).trim() : "");

  const singlePointRoleId =
    (hv(h?.cinglepointRoleId) ? String(h.cinglepointRoleId).trim() : "") ||
    (hv(h?.singlepointRoleId) ? String(h.singlepointRoleId).trim() : "");

  const effectiveCpRoleId = String(
    cpRoleIdFromHinge || singlePointRoleId || "",
  ).trim();

  const cpRoleDescFromHinge =
    (hv(h?.attRoleCpRoleDesc) ? String(h.attRoleCpRoleDesc).trim() : "") ||
    (hv(h?.att_role_cp_role_desc)
      ? String(h.att_role_cp_role_desc).trim()
      : "") ||
    (hv(h?.cinglepointRoleDesc) ? String(h.cinglepointRoleDesc).trim() : "");

  const effectiveCpRoleDesc = String(
    cpRoleDescFromHinge || effectiveCpRoleId || "",
  ).trim();

  return {
    // overall status
    status: h.personStatus || "",
    dtvidStatus: h.personStatus ?? null,
    attuidStatus: h?.attuidStatus ?? h?.attuid_status ?? null,
    attuidDeactivatedDate:
      h?.attuidDeactivatedDate ?? h?.attuid_deactivated_date ?? null,
    attuidSupervisor: h?.attuidSupervisor ?? h?.attuid_supervisor ?? null,

    // IMPORTANT: keep "security_id" for UI header (masked)
    security_id: h?.ssnLast4d || h.dtvAnchorId || "",

    // submission
    submitter_name: h.nominatorName || "",
    requested_by: h.nominatorName || "",
    requested_on: h.submissionDate || "",

    // personal
    first_name: h.firstName || "",
    last_name: h.lastName || "",
    email: h.secondaryEmail || h.primaryEmail || "",
    phone: h.phone || "",
    citizenship: h.countryOfCitizenship || "",

    // ids
    dtv_id: h.dtvAnchorId || "",
    attuid: h.attuid || "",

    // location (best-effort)
    state: hingeState,
    city: hingeCity,
    country: hingeCountry,

    // supplier
    supplier_reference: h.supplierReference || h.contractNumber || "",
    vendor: h.supplierName || "",
    npw_manager_company: h.supplierName || "",

    // sponsorship & role
    sponsor: h.employeeSponsor || "",
    role: h.dtvRoleName || "",
    time_zone: h.timeZone || "",
    occupancy,
    webphone_manager: h.supervisorDtvAnchorId || h.supervisorAttuid || "",
    employment_type: h.personType || "",
    non_billable: nonBillable,

    // sponsor approval lane
    dtv_approved: sponsorApproved,
    dtv_approver_name: h.approverName || "",
    dtv_approver_datetime: h.approvalDate || "",
    dtv_approver_id: h.approverDtvId || "",
    // Comments from lookup API (rendered in Approver Comments panel)
    approve_comments: h.approvalComments || "",
    __is_lookup: true,
    __lookup_sponsor_ok: sponsorApproved,
    __lookup_cp_needed: attReq,
    __lookup_person_type: personTypeRaw,
    __force_cp_only: forceCpOnly,
    __lookup_is_attuid_requested_raw: h?.isAttuidRequested,
    isSecExist:
      h?.isSecExist ??
      h?.isSecExists ??
      h?.is_sec_exist ??
      h?.is_sec_exists ??
      h?.isSecurityExist ??
      h?.isSecurityExists ??
      h?.securityExists ??
      h?.security_exist ??
      h?.securityIdExists ??
      "",
    is_sec_exist:
      h?.is_sec_exist ??
      h?.is_sec_exists ??
      h?.isSecExist ??
      h?.isSecExists ??
      h?.security_exist ??
      h?.securityExists ??
      "",
    __lookup_raw: h,

    // CP / ATTUID approval lane
    attuid_required: attReq,
    attuid_required_flag: attReq,
    employee_sponsor_attuid_requested: cpApproved ? true : undefined,

    attuid_approved: cpApproved,
    attuid_approver_name: h.attuidApproverName || "",
    attuid_approver_datetime: h.attuidApprovalDate || "",
    // Comments from lookup API (rendered in Approver Comments panel)
    attuid_approve_comments: h.attuidApprovalComments || "",

    // FTE/CP only
    fte_nominated: fteNom,
    att_role_cp_role_id: effectiveCpRoleId,
    att_role_cp_role_desc: effectiveCpRoleDesc,

    // contract dates
    contract: h.contractNumber || "",
    contract_po: h.contractNumber || "",
    po_number: h.poNumber || "",
    line_number: h.lineNumber || "",
    start_date: h.effectiveStartDate || "",
    end_date: h.effectiveEndDate || "",

    // Keep raw identity fields so UI lane-logic can use consistent keys
    employeeSponsor: h.employeeSponsor || "",
    nominatorDtvId: h.nominatorDtvId || "",
    nominatorEmailId: h.nominatorEmailId || "",
    approverDtvId: h.approverDtvId || "",
    approverEmailId: h.approverEmailId || "",

    approver_comments: [],
  };
}

/* ===== email validator (for reminder) ===== */
function looksLikeEmail(s) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || "").trim());
}

/* ===== small util: dedupe list ===== */
function dedupe(arr) {
  const seen = new Set();
  const out = [];
  (arr || []).forEach((v) => {
    const s = String(v || "")
      .trim()
      .toLowerCase();
    if (!s) return;
    if (seen.has(s)) return;
    seen.add(s);
    out.push(String(v).trim());
  });
  return out;
}

const US_TEMPLATE = "   -  -    ";
const US_DIGIT_POS = [0, 1, 2, 4, 5, 7, 8, 9, 10];
const NONUS_TEMPLATE = "f-  -  -    ";
const NONUS_DIGIT_POS = [2, 3, 5, 6, 8, 9, 10, 11];

const templateFor = (isUS) => (isUS ? US_TEMPLATE : NONUS_TEMPLATE);
const digitPosFor = (isUS) => (isUS ? US_DIGIT_POS : NONUS_DIGIT_POS);
const maxDigitsFor = (isUS) => (isUS ? 9 : 8);

function extractDigitsByRegion(value, isUS) {
  const str = String(value || "");
  if (isUS) return str.replace(/\D/g, "").slice(0, 9);

  let out = "";
  for (let i = 0; i < str.length && out.length < 8; i++) {
    const ch = str[i];
    if (out.length < 4) {
      if (/\d/.test(ch)) out += ch;
    } else if (/\w/.test(ch)) {
      out += ch.toUpperCase();
    }
  }
  return out;
}

function buildMaskedDisplay(isUS, digits) {
  const tpl = templateFor(isUS).split("");
  const pos = digitPosFor(isUS);
  const safe = String(digits || "");
  for (let i = 0; i < pos.length; i++)
    tpl[pos[i]] = i < safe.length ? "*" : "_";
  return tpl.join("");
}

function buildUnmaskedFormatted(isUS, digits) {
  const d = extractDigitsByRegion(digits, isUS);
  if (!d) return "";
  if (isUS) {
    const a = d.slice(0, 3);
    const b = d.slice(3, 5);
    const c = d.slice(5, 9);
    return `${a}${d.length > 3 ? "-" : ""}${b}${d.length > 5 ? "-" : ""}${c}`.replace(
      /-$/,
      "",
    );
  }
  const a = d.slice(0, 2);
  const b = d.slice(2, 4);
  const c = d.slice(4, 8);
  return `f-${a}${d.length > 2 ? "-" : ""}${b}${d.length > 4 ? "-" : ""}${c}`.replace(
    /-$/,
    "",
  );
}

function normalizeIsSecExist(v) {
  const s = String(v ?? "")
    .trim()
    .toLowerCase();
  if (["yes", "y", "true", "1", "t"].includes(s)) return "yes";
  if (["no", "n", "false", "0", "f"].includes(s)) return "no";
  return "";
}

function resolveIsSecExistValue(src) {
  if (!src || typeof src !== "object") return "";
  const value =
    src?.isSecExist ??
    src?.isSecExists ??
    src?.isSecExitst ??
    src?.isSecExits ??
    src?.is_sec_exist ??
    src?.is_sec_exists ??
    src?.isSecurityExist ??
    src?.isSecurityExists ??
    src?.securityExists ??
    src?.security_exist ??
    src?.securityIdExists ??
    src?.response?.isSecExist ??
    src?.response?.isSecExists ??
    src?.response?.isSecExitst ??
    src?.response?.isSecExits ??
    src?.response?.is_sec_exist ??
    src?.response?.is_sec_exists ??
    src?.response?.isSecurityExist ??
    src?.response?.isSecurityExists ??
    src?.response?.securityExists ??
    src?.response?.security_exist ??
    src?.response?.securityIdExists ??
    src?.form1?.isSecExist ??
    src?.form1?.isSecExists ??
    src?.form1?.isSecExitst ??
    src?.form1?.isSecExits ??
    src?.form1?.is_sec_exist ??
    src?.form1?.is_sec_exists ??
    src?.form1?.securityExists ??
    src?.form1?.security_exist ??
    src?.data?.isSecExist ??
    src?.data?.isSecExists ??
    src?.data?.isSecExitst ??
    src?.data?.isSecExits ??
    src?.data?.is_sec_exist ??
    src?.data?.is_sec_exists;

  const normalizedDirect = normalizeIsSecExist(value);
  if (normalizedDirect) return normalizedDirect;

  const pickByLooseKey = (obj) => {
    if (!obj || typeof obj !== "object") return "";
    const targetKeys = new Set([
      "issecexist",
      "issecexists",
      "issecexitst",
      "issecexits",
      "issecurityexist",
      "issecurityexists",
      "securityexists",
      "securityexist",
      "securityidexists",
    ]);
    for (const key of Object.keys(obj)) {
      const normalizedKey = String(key || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
      if (!targetKeys.has(normalizedKey)) continue;
      const normalizedVal = normalizeIsSecExist(obj[key]);
      if (normalizedVal) return normalizedVal;
    }
    return "";
  };

  return (
    pickByLooseKey(src) ||
    pickByLooseKey(src?.response) ||
    pickByLooseKey(src?.form1) ||
    pickByLooseKey(src?.data) ||
    ""
  );
}

function resolveSecurityDigits(src, isUS) {
  if (!src || typeof src !== "object") return "";
  const candidates = [
    src.security_id_plaintext,
    src.securityIdPlaintext,
    src.security_id_unmasked,
    src.securityIdUnmasked,
    src.security_id_value,
    src.securityIdValue,
    src.security_id_raw,
    src.securityIdRaw,
    src.security_id,
    src.securityId,
    src.ssnLast4d,
    src.ssn_last4d,
    src.ssnLast4,
    src.ssn_last4,
  ];
  for (const val of candidates) {
    const d = extractDigitsByRegion(val, isUS);
    if (d) return d;
  }
  return "";
}

function getManagerValidationPayload(resp) {
  if (!resp || typeof resp !== "object") return resp;
  if (resp.response && typeof resp.response === "object") return resp.response;
  if (resp.data && typeof resp.data === "object") return resp.data;
  return resp;
}
function getManagerAttuidFromResp(resp) {
  const r = getManagerValidationPayload(resp);
  return firstNonEmpty(r?.attuid, r?.attUid, r?.attUID);
}
function getManagerAttuidStatusRawFromResp(resp) {
  const r = getManagerValidationPayload(resp);
  return (
    r?.attuidStatus ??
    r?.attuidstatus ??
    r?.attuid_status ??
    r?.attUidStatus ??
    r?.attUIDStatus ??
    r?.personStatus ??
    r?.person_status ??
    r?.status ??
    ""
  );
}
function isActiveManagerAttuidResp(resp) {
  const r = getManagerValidationPayload(resp);
  if (!r || r?.isValid === false) return false;
  const attuid = getManagerAttuidFromResp(r);
  if (!attuid || String(attuid).trim().toLowerCase() === "false") return false;

  const rawStatus = getManagerAttuidStatusRawFromResp(r);
  if (rawStatus === true || rawStatus === 1) return true;

  const status = String(rawStatus ?? "")
    .trim()
    .toLowerCase();
  return ["active", "true", "t", "yes", "y", "1"].includes(status);
}
function getManagerEmailFromResp(resp) {
  const r = getManagerValidationPayload(resp);
  return firstNonEmpty(r?.emailId, r?.email, r?.emailAddress);
}
function getReactivateManagerId(sourceDetails, data) {
  return firstNonEmpty(
    sourceDetails?.supervisorDtvAnchorId,
    sourceDetails?.supervisor_dtv_anchor_id,
    sourceDetails?.webphoneManagerId,
    sourceDetails?.webphone_manager,
    data?.webphone_manager,
    data?.webphoneManagerId,
  );
}
async function validateReactivateWebphoneManager(sourceDetails, data) {
  const managerId = getReactivateManagerId(sourceDetails, data);
  if (!managerId) {
    return {
      managerId: "",
      webphoneManagerAttuid: null,
      webphoneManagerEmail: "",
      status: "idle",
      message: "",
      response: null,
    };
  }
  if (!TB_HINGE_WEBPHONE_VALIDATE_URL) {
    return {
      managerId,
      webphoneManagerAttuid: null,
      webphoneManagerEmail: "",
      status: "error",
      message: "Webphone Manager validation URL is not configured",
      response: null,
    };
  }

  const base = TB_HINGE_WEBPHONE_VALIDATE_URL;
  const sep = base.includes("?") ? "&" : "?";
  const resp = await tbGetJson(
    `${base}${sep}mgrDtvId=${encodeURIComponent(managerId)}`,
  );
  const active = isActiveManagerAttuidResp(resp);
  const attuid = active ? getManagerAttuidFromResp(resp) : "";
  const email = active ? getManagerEmailFromResp(resp) : "";
  return {
    managerId,
    webphoneManagerAttuid: attuid || null,
    webphoneManagerEmail: email,
    status: active ? "ok" : "error",
    message: active
      ? ""
      : "Webphone Manager does not have an active ATTUID in Hinge",
    response: resp,
  };
}

function getReactivateValidationErrors({
  securityDigits,
  confirmDigits,
  isUS,
  securityLocked,
  securityLabel,
  confirmLabel,
}) {
  if (securityLocked) return { securityId: "", confirmSecurityId: "" };

  let securityId = "";
  let confirmSecurityId = "";
  const maxDigits = maxDigitsFor(isUS);

  if (!securityDigits) securityId = `${securityLabel} is required`;
  else if (securityDigits.length !== maxDigits)
    securityId = `${securityLabel} must be ${maxDigits} ${isUS ? "digits" : "characters"}`;
  else if (!isUS) {
    const mmddMsg = validateSecurityIdMmdd8(securityDigits);
    if (mmddMsg) securityId = mmddMsg;
  }

  if (!confirmDigits) confirmSecurityId = `${confirmLabel} is required`;
  else if (confirmDigits.length !== maxDigits)
    confirmSecurityId = `${confirmLabel} must be ${maxDigits} ${isUS ? "digits" : "characters"}`;
  else if (!isUS) {
    const mmddMsg = validateSecurityIdMmdd8(confirmDigits);
    if (mmddMsg) confirmSecurityId = mmddMsg;
  }

  if (!securityId && !confirmSecurityId && securityDigits !== confirmDigits) {
    confirmSecurityId = `${confirmLabel} must match ${securityLabel}`;
  }

  return { securityId, confirmSecurityId };
}

/* ========= Failure Details (same behavior as ApprovalDetail) ========= */
const normalizeStatusKey = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, "");

const formatStatusLabel = (v, { notAssigned = false } = {}) => {
  const raw = String(v ?? "").trim();
  if (
    !raw ||
    raw.toLowerCase() === "null" ||
    raw.toLowerCase() === "undefined"
  ) {
    return notAssigned ? "Not Assigned" : "";
  }
  return raw
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

function pickFailureDetails(obj) {
  if (!obj || typeof obj !== "object") return "";
  const st = normalizeStatusKey(obj.status);
  if (st !== "failed" && st !== "failure") return "";

  // Prefer explicit failure messages in priority order
  const parts = [];
  const push = (label, val) => {
    const v = String(val || "").trim();
    if (!v) return;
    parts.push(`${label}: ${v}`);
  };

  push("Hinge", obj.hinge_failure_message || obj.hingeFailureMessage);
  push("CP", obj.cp_failure_message || obj.cpFailureMessage);
  push("DTV", obj.dtv_failure_message || obj.dtvFailureMessage);

  // Fallbacks if backend ever sends a generic message field
  if (!parts.length) {
    const generic =
      obj.failure_message ||
      obj.failureMessage ||
      obj.error_message ||
      obj.errorMessage ||
      obj.message ||
      "";
    const v = String(generic || "").trim();
    if (v) parts.push(v);
  }

  return parts.join(" | ");
}

export default function RequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  // ---- Robust id parse (NPWID/DTVID in path or as query) ----
  const rawParamId = decodeURIComponent(id || "");
  let npwId = "";
  let dtvId = "";
  if (rawParamId) {
    if (rawParamId.startsWith("DTVID="))
      dtvId = rawParamId.substring("DTVID=".length);
    else if (rawParamId.startsWith("NPWID="))
      npwId = rawParamId.substring("NPWID=".length);
    else {
      const v = String(rawParamId || "").trim();
      if (/^\d+$/.test(v)) npwId = v;
      else dtvId = v;
    }
  }
  try {
    const usp = new URLSearchParams(window.location.search || "");
    const qpDtv = usp.get("dtvid") || usp.get("dtvId") || usp.get("DTVID");
    const qpNpw = usp.get("npw_id") || usp.get("npwid") || usp.get("NPWID");
    if (!dtvId && qpDtv) dtvId = qpDtv;
    if (!npwId && qpNpw && !dtvId) {
      const v = String(qpNpw || "").trim();
      if (/^\d+$/.test(v)) npwId = v;
      else dtvId = v;
    }
  } catch {}

  const [data, setData] = useState(null);

  // Failure details (match ApprovalDetail behavior)
  const failureDetails = useMemo(() => pickFailureDetails(data), [data]);
  const showFailureDetails = Boolean(failureDetails);

  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isOffboardModalOpen, setIsOffboardModalOpen] = useState(false);
  const [offboarding, setOffboarding] = useState(false);
  const [offboardEndDate, setOffboardEndDate] = useState("");
  const [offboardNote, setOffboardNote] = useState("");
  const [offboardIdType, setOffboardIdType] = useState("DTVID");
  const [offBoardDateError, setOffboardDateError] = useState("");
  const [offBoardError, setOffboardError] = useState("");
  const [infoReqSubmitting, setInfoReqSubmitting] = useState(false);
  const [infoReqComment, setInfoReqComment] = useState("");
  const [detailsReloadTick, setDetailsReloadTick] = useState(0);
  /*TODO:pentest*/ const [loadingDetailFails, setLoadingDetailFails] =
    useState(false);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [reactivateSubmitting, setReactivateSubmitting] = useState(false);
  const [reactivateLookup, setReactivateLookup] = useState(null);
  const [reactivateSecurityId, setReactivateSecurityId] = useState("");
  const [reactivateConfirmSecurityId, setReactivateConfirmSecurityId] =
    useState("");
  const [reactivateShowSecurity, setReactivateShowSecurity] = useState(false);
  const [reactivateShowConfirmSecurity, setReactivateShowConfirmSecurity] =
    useState(false);
  const [reactivateSecurityEditEnabled, setReactivateSecurityEditEnabled] =
    useState(false);
  const [reactivateInitialSecurityDigits, setReactivateInitialSecurityDigits] =
    useState("");
  const [reactivateSecExistValue, setReactivateSecExistValue] = useState("");
  const [reactivateAssociation, setReactivateAssociation] = useState({
    status: "idle",
    message: "",
  });
  const [, setReactivateWebphoneValidation] = useState({
    status: "idle",
    message: "",
  });
  const [reactivateWebphoneManager, setReactivateWebphoneManager] = useState({
    managerId: "",
    webphoneManagerAttuid: null,
    webphoneManagerEmail: "",
  });
  const [reactivateCpValidation, setReactivateCpValidation] = useState({
    status: "idle",
    message: "",
  });
  const [reactivateValidating, setReactivateValidating] = useState(false);
  const reactivateAssocDebounceRef = useRef(null);
  const reactivateAssocSeqRef = useRef(0);
  const reactivateValidateSeqRef = useRef(0);

  const gridRef = useRef(null);
  const scopeRef = useRef(null);

  /* initial load (refresh on every id change) */
  const _activeLoadRef = useRef(0);
  useEffect(() => {
    const loadId = ++_activeLoadRef.current;

    (async () => {
      try {
        setLoading(true);
        setData(null);
        /*TODO:pentest*/ setLoadingDetailFails(false);

        if (dtvId) {
          // Prefer TB-Hinge proxy so Hinge APIs are not exposed to the browser.
          const base =
            TB_HINGE_PERSON_LOOKUP_URL ||
            ENV.LOOKUP_URL ||
            (ENV.HINGE_BASE
              ? `${ENV.HINGE_BASE.replace(/\/$/, "")}/person/lookup`
              : "");

          if (!base) {
            toast.error("Hinge/TB-Hinge lookup URL is not configured.", {
              toastId: "hinge-env-missing",
              position: "top-right",
            });
            if (!npwId) return;
          } else {
            const loginDtvIdValue = encodeLoggedInUserDtvId(
              sessionStorage.getItem("tb_dtvid") || "",
            );
            const roleRaw =
              sessionStorage.getItem("tb_role") ||
              sessionStorage.getItem("tb_role_raw") ||
              "";
            const url =
              `${base}${base.includes("?") ? "&" : "?"}` +
              `dtvId=${encodeURIComponent(dtvId)}` +
              (loginDtvIdValue
                ? `&loggedInUserDtvId=${encodeURIComponent(loginDtvIdValue)}`
                : "") +
              `&loggedInUserRole=${encodeURIComponent(roleRaw)}` +
              `&_=${Date.now()}`;

            try {
              const payload = await tbGetJson(url);
              if (_activeLoadRef.current !== loadId) return;
              const inner = unwrapLookup(payload);

              const mapped = mapHingeToDetail(inner);
              const sponsorApproved = truthy(mapped?.dtv_approved);
              mapped.__comments_flat = collectComments(mapped, sponsorApproved);
              setData(mapped);
              return; // lookup view handled
            } catch (e) {
              /*TODO:pentest*/ setLoadingDetailFails(true);
              console.warn(
                "Lookup failed; falling back to Approval Details when available.",
                e,
              );
              if (!npwId) {
                toast.error(
                  "Failed to load via lookup and no NPWID available for fallback.",
                  {
                    toastId: "lookup-failed-no-npw",
                    position: "top-right",
                  },
                );
                return;
              }
            }
          }
        }

        if (!DETAILS_URL) {
          toast.error("Approval details URL is not configured.", {
            toastId: "env-missing",
            position: "top-right",
          });
          return;
        }
        if (!npwId) {
          toast.error("No request id provided.", {
            toastId: "npw-missing",
            position: "top-right",
          });
          return;
        }

        /*TODO:pentest*/
        // Build v query
        const dtvidRaw = sessionStorage.getItem("tb_dtvid") || "";
        const roleRaw =
          sessionStorage.getItem("tb_role") ||
          sessionStorage.getItem("tb_role_raw") ||
          "";
        const ts = Date.now();
        const vRaw = `${dtvidRaw}_*v*_${ts}`;
        const vParam =
          typeof btoa === "function" ? btoa(vRaw) : window.btoa(vRaw);

        const base = DETAILS_URL.replace(/\/$/, "");
        const loginDtvIdValue = encodeLoggedInUserDtvId(dtvidRaw);
        /*TODO:pentest*/ const url =
          `${base}?npw_id=${encodeURIComponent(npwId)}` +
          `&role=${encodeURIComponent(roleRaw)}` +
          (loginDtvIdValue
            ? `&loggedInUserDtvId=${encodeURIComponent(loginDtvIdValue)}`
            : "") +
          `&v=${encodeURIComponent(vParam)}&_=${Date.now()}`;
        // const url = `${base}?npw_id=${encodeURIComponent(npwId)}&_=${Date.now()}`; //TODO:pentest comment this and uncomment above
        const raw = (await tbGetJson(url)) || {};
        if (_activeLoadRef.current !== loadId) return;
        const d = mapApprovalDetailsToDetail(raw) || {};
        const sponsorApproved = truthy(d?.dtv_approved);
        d.__comments_flat = collectComments(d, sponsorApproved);
        setData(d);
      } catch (e) {
        /*TODO:pentest*/ setLoadingDetailFails(true);
        console.error(e);
        toast.error("Failed to load details.", {
          toastId: "load-error",
          position: "top-right",
        });
      } finally {
        if (_activeLoadRef.current === loadId) {
          setLoading(false);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dtvId, npwId, detailsReloadTick]);

  /* sticky rail geometry (match ApprovalDetail) */
  useLayoutEffect(() => {
    function align() {
      const grid = gridRef.current;
      if (!grid) return;
      const rect = grid.getBoundingClientRect();
      const left = rect.left + window.scrollX;
      const width = rect.width;
      const root = document.documentElement;
      root.style.setProperty("--rd-left-fixed", `${left}px`);
      root.style.setProperty("--rd-width-fixed", `${width}px`);
    }
    function onScroll() {}
    align();
    window.addEventListener("resize", align);
    const scope =
      scopeRef.current || document.querySelector(".page-scroll-scope");
    scope?.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("resize", align);
      scope?.removeEventListener("scroll", onScroll);
    };
  }, [loading]);

  /* ===== status & flow flags ===== */
  const fteNominated = truthy(data?.fte_nominated);

  const fteNominatedEmpty =
    data?.fte_nominated === undefined ||
    data?.fte_nominated === null ||
    String(data?.fte_nominated).trim() === "";

  // Sponsor lane may arrive under different field names depending on the backend (DB vs Hinge wrapper).
  // IMPORTANT:
  // - In true one-level flows, sponsor and nominator can be the SAME person (ex: FTE nominator self-approves),
  //   and we should NOT render a separate Sponsor step.
  // - Therefore, treat "sponsor lane present" only when sponsor identity exists AND is DISTINCT from nominator.
  const sponsorId = firstNonEmpty(
    data?.employeeSponsor,
    data?.employee_sponsor,
    data?.dtv_approver_id,
    data?.approverDtvId,
    data?.sponsor_dtv_id,
    data?.sponsorDtvId,
  );
  const sponsorEmail = firstNonEmpty(
    data?.sponsor_email,
    data?.sponsorEmail,
    data?.dtv_approver_email,
    data?.approverEmailId,
  );
  const sponsorName = firstNonEmpty(
    data?.dtv_approver_name,
    data?.approverName,
  );

  const sponsorIdentityPresent =
    hasVal(sponsorId) || hasVal(sponsorEmail) || hasVal(sponsorName);

  // Sponsor approval lane exists whenever sponsor identity exists.
  // Sponsor and nominator MAY be the same person (self-approve), and we still show the Sponsor lane.
  const sponsorLanePresent = sponsorIdentityPresent;

  // ATTUID-required should also light up for "ATTUID requested" style fields.
  const attuidRequired =
    truthy(data?.attuid_required) ||
    truthy(data?.attuid_required_flag) ||
    truthy(data?.isAttuidRequested) ||
    truthy(data?.is_attuid_requested) ||
    fteNominated;

  const isUS = isUSCountry(data?.country);
  const secIdLabel = isUS && attuidRequired ? "SSN" : "Security ID";

  // Offboarding drivers (same routing model as ApprovalDetail)
  const offboardByDtvId = truthy(data?.offboard_by_dtv_id);
  const offboardByAttuid = truthy(data?.offboard_by_attuid);
  const hasAttuidInFlow = hasVal(data?.attuid);

  const hideFteSections = fteNominated === true;

  const sponsor = {
    approved: truthy(data?.dtv_approved),
    rejected: truthy(data?.dtv_rejected),
    sendback: truthy(data?.dtv_sendback),
    whoA: data?.dtv_approver_name || data?.approver_name,
    whenA: data?.dtv_approver_datetime || data?.approved_on,
    whoR: data?.dtv_rejected_name || data?.rejected_name,
    whenR: data?.dtv_rejected_datetime || data?.rejected_on,
    whoS: data?.dtv_sendback_name || data?.sendback_name,
    whenS: data?.dtv_sendback_datetime || data?.sendback_on,
  };

  const cp = {
    approved: truthy(data?.attuid_approved),
    rejected: truthy(data?.attuid_rejected),
    sendback: truthy(data?.attuid_sendback),
    whoA: data?.attuid_approver_name,
    whenA: data?.attuid_approver_datetime,
    whoR: data?.attuid_rejected_name,
    whenR: data?.attuid_rejected_datetime,
    whoS: data?.attuid_sendback_name,
    whenS: data?.attuid_sendback_datetime,
  };

  const sponsorAttChoice = data?.employee_sponsor_attuid_requested; // true|false|undefined

  // Sponsor chose "ATTUID required = No" applies ONLY to onboarding paths (not offboarding paths)
  const sponsorChoseNo =
    !offboardByDtvId &&
    !offboardByAttuid &&
    attuidRequired &&
    sponsor.approved &&
    sponsorAttChoice === false;

  const forceCpOnly = truthy(data?.__force_cp_only);

  // Reactivate ATTUID requests follow the CP-only (FTE/CP) approval rail.
  const yesTrue = (v) =>
    truthy(v) ||
    ["yes", "y"].includes(
      String(v ?? "")
        .trim()
        .toLowerCase(),
    );
  const isReactivateAttuidRequest =
    yesTrue(data?.isReactivateAttuid) ||
    yesTrue(data?.reactivate_attuid) ||
    yesTrue(data?.reactive_attuid) ||
    yesTrue(data?.is_reactivate_attuid) ||
    yesTrue(data?.__lookup_raw?.isReactivateAttuid) ||
    yesTrue(data?.__lookup_raw?.reactivate_attuid);

  /*
    /*
      Flow routing precedence (must preserve 2nd step when required):

      1) FTE nominated => CP Delegate only (one-level)
      2) Offboard by ATTUID => CP Delegate only (one-level)
      3) Offboard by DTVID:
           - if ATTUID exists => two-level (Sponsor -> CP Delegate)
           - else => sponsor-only
      4) Onboarding:
           - if ATTUID not required OR sponsor chose no => sponsor-only
           - else => two-level
    */
  let flow = "sponsor-only";

  if (fteNominated || forceCpOnly || isReactivateAttuidRequest)
    flow = "cp-only";
  // else if (fteNominated || forceCpOnly) flow = "cp-only";
  else if (offboardByAttuid) flow = "cp-only";
  else if (offboardByDtvId)
    flow = hasAttuidInFlow ? "two-level" : "sponsor-only";
  else if (!attuidRequired) flow = "sponsor-only";
  else if (sponsorChoseNo) flow = "sponsor-only";
  else flow = "two-level";

  const sponsorVM = {
    title: "Employee Sponsor",
    lane: sponsor,
    name: sponsor.approved
      ? sponsor.whoA
      : sponsor.rejected
        ? sponsor.whoR
        : sponsor.sendback
          ? sponsor.whoS
          : "",
    when: sponsor.approved
      ? sponsor.whenA
      : sponsor.rejected
        ? sponsor.whenR
        : sponsor.sendback
          ? sponsor.whenS
          : "",
  };

  const cpTile = (() => {
    if (flow === "sponsor-only") return null;

    let lane = cp;
    let name = cp.approved
      ? cp.whoA
      : cp.rejected
        ? cp.whoR
        : cp.sendback
          ? cp.whoS
          : "";
    let when = cp.approved
      ? cp.whenA
      : cp.rejected
        ? cp.whenR
        : cp.sendback
          ? cp.whenS
          : "";
    let note = "";

    // If sponsor rejected, show CP tile as "Rejected" (visual only) with explanatory note.
    if (sponsor.rejected) {
      note = "Employee Sponsor rejected the DTVID request";
      lane = { approved: false, rejected: true, sendback: false };
      name = "";
      when = "";
    } else if (flow === "two-level" && sponsor.approved && cp.rejected) {
      note = "CP Delegate rejected the ATTUID request";
      lane = { approved: false, rejected: true, sendback: false };
    } else {
      if (
        flow === "two-level" &&
        sponsor.approved &&
        !cp.approved &&
        !cp.rejected &&
        !cp.sendback
      ) {
        note = "Pending approval";
      } else if (sponsor.sendback) {
        note = "Employee Sponsor requested more info";
      }
    }

    return { lane, name, when, note };
  })();

  function finalMsg() {
    const sA = sponsor.approved;
    const sR = sponsor.rejected;
    const sI = sponsor.sendback;

    const cA = cp.approved;
    const cR = cp.rejected;
    const cI = cp.sendback;

    if (sR)
      return {
        label: "Rejected",
        note: "Employee Sponsor / ESA rejected the request",
        tone: "bad",
      };
    if (sI)
      return {
        label: "Info Requested",
        note: "Employee Sponsor / ESA requested more info",
        tone: "warm",
      };

    const fgSubmitted = isFgSubmitted(
      data?.submitter_name || data?.requested_by,
    );

    // CP-only flow
    if (flow === "cp-only") {
      if (cA) {
        return {
          label: "Approved",
          note: fgSubmitted
            ? "Approved by FG Approver"
            : offboardByAttuid
              ? "CP Delegate approved. ATTUID offboarding submitted."
              : "CP Delegate approved. ATTUID onboarding request submitted.",
          tone: "ok",
        };
      }
      if (cR)
        return {
          label: "Rejected",
          note: "CP Delegate rejected the request",
          tone: "bad",
        };
      if (cI)
        return {
          label: "Info Requested",
          note: "CP Delegate requested more info",
          tone: "warm",
        };

      return {
        label: "Pending",
        note: "Pending with CP Delegate",
        tone: "neutral",
      };
    }

    if (sponsorChoseNo) {
      if (sA) {
        return {
          label: "Approved",
          note: fgSubmitted
            ? "Approved by FG Approver"
            : "Employee Sponsor rejected ATTUID request. Request for DTV ID / Hierarchy Creation sent.",
          tone: "ok",
        };
      }
      return {
        label: "Pending",
        note: "Pending with Employee Sponsor",
        tone: "neutral",
      };
    }

    if (flow === "two-level") {
      if (sA && cA) {
        return {
          label: "Approved",
          note: fgSubmitted
            ? "Approved by FG Approver"
            : offboardByDtvId
              ? "Approvals completed. Offboarding submitted for DTVID and ATTUID."
              : "Approvals completed. Request for DTV ID / ATTUID Creation / Hierarchy Creation sent.",
          tone: "ok",
        };
      }
      if (sA && cR)
        return {
          label: "Rejected",
          note: "CP Delegate rejected the request",
          tone: "bad",
        };
      if (sA && cI)
        return {
          label: "Info Requested",
          note: "CP Delegate requested more info",
          tone: "warm",
        };

      // Sponsor approved, CP still pending -> show DTVID/Hinge initiated + CP pending.
      if (sA && !cA && !cR && !cI) {
        return {
          label: "Pending",
          note: fgSubmitted
            ? "Approved by FG Approver"
            : offboardByDtvId
              ? "Employee Sponsor approved. Offboarding initiated for DTVID. Pending with CP Delegate."
              : "Employee Sponsor approved. DTVID/Hinge Hierarchy placements initiated. Pending with CP Delegate.",
          tone: "neutral",
        };
      }

      if (!sA)
        return {
          label: "Pending",
          note: "Pending with Employee Sponsor",
          tone: "neutral",
        };
      return {
        label: "Pending",
        note: "CP Delegate approval pending",
        tone: "neutral",
      };
    }

    // sponsor-only
    if (sA) {
      return {
        label: "Approved",
        note: fgSubmitted
          ? "Approved by FG Approver"
          : offboardByDtvId
            ? "Approved by Employee Sponsor / ESA. DTVID offboarding initiated."
            : "Approved by Employee Sponsor / ESA. DTVID/Hinge Hierarchy placements initiated.",
        tone: "ok",
      };
    }

    return { label: "Pending", note: "Pending with approver", tone: "neutral" };
  }

  const final = finalMsg();
  const finalTint =
    final.tone === "ok"
      ? tint({ approved: true })
      : final.tone === "bad"
        ? tint({ rejected: true })
        : final.tone === "warm"
          ? tint({ sendback: true })
          : {};

  // Enable reminders ONLY for these statuses:
  // - Submitted
  // - Submitted to CP
  // - Offboarding Submitted
  // - Offboarding Submitted to CP
  const REMINDER_ALLOWED_STATUSES = [
    "submitted",
    "submittedtocp",
    "offboardingsubmitted",
    "offboardingsubmittedtocp",
  ];
  const reminderStatusKey = normalizeStatusKey(data?.status);
  const reminderBlocked =
    !REMINDER_ALLOWED_STATUSES.includes(reminderStatusKey);

  // =========================================================
  // NEW: Determine if reminder should go to CP Delegate group
  // =========================================================
  const pendingWithCpDelegate =
    !reminderBlocked &&
    // CP-only flow pending
    ((flow === "cp-only" && !cp.approved && !cp.rejected && !cp.sendback) ||
      // Two-level flow pending AFTER sponsor approval
      (flow === "two-level" &&
        sponsor.approved &&
        !cp.approved &&
        !cp.rejected &&
        !cp.sendback));

  // CP reminder emails are gated by CP_EMAIL_ALLOWED; block only the CP-delegate case.
  const cpEmailDisabled = pendingWithCpDelegate && !CP_EMAIL_ALLOWED;

  // =========================================================
  // NEW: Call list API and collect CP Delegate emails
  // =========================================================
  async function getCpDelegateEmails() {
    if (!TB_HINGE_PERSON_LIST_URL) {
      toast.error("CP Delegate list API URL is not configured.", {
        toastId: "cp-list-missing",
        position: "top-right",
      });
      return [];
    }

    // 🔹 REQUEST payload (do NOT reuse name)
    const callerRole = String(sessionStorage.getItem("tb_role") || "").trim();
    // const isNominator = callerRole.toLowerCase() === "nominator";
    const vendorName = String(
      sessionStorage.getItem("tb_supplierName") ||
        sessionStorage.getItem("tb_vendorId") ||
        "",
    ).trim();
    // const nominatorDtvId = String(sessionStorage.getItem("tb_dtvid") || "").trim();
    const requestBody = {
      correlationId: makeCorrelationId(),
      sourceSystemCode: "1",
      searchCriteria: {
        //  ...(callerRole ? { roleName: callerRole } : {}),
        isCPChannelDelegate: true,
        //   ...(isNominator && vendorName ? { userVendorName: vendorName } : {}),
      },
      pagination: { pageNumber: "1", pageSize: "50" },
    };

    try {
      const res = await tbPostJson(TB_HINGE_PERSON_LIST_URL, requestBody);

      // 🔹 RESPONSE payload (separate variable)
      const responseBody = res?.response || res;
      const list = Array.isArray(responseBody?.personList)
        ? responseBody.personList
        : [];

      const emails = list
        .map((p) => String(p?.primaryEmail || "").trim())
        .filter((e) => looksLikeEmail(e));

      return dedupe(emails);
    } catch (e) {
      console.error("CP Delegate list API failed:", e);
      toast.error("Failed to load CP Delegate email list.", {
        toastId: "cp-list-failed",
        position: "top-right",
      });
      return [];
    }
  }

  // ============================
  // sendReminder (updated)
  // ============================
  async function sendReminder() {
    if (!EMAIL_NOTIFY_URL) {
      toast.error("Email notifications endpoint not configured.", {
        toastId: "email-endpoint-missing",
        position: "top-right",
      });
      return;
    }
    if (!npwId && !rawParamId) {
      toast.error("No request id to include in reminder.", {
        toastId: "email-id-missing",
        position: "top-right",
      });
      return;
    }
    if (reminderBlocked) return;

    const idForLink = npwId || rawParamId;
    const linkPath = `approvals/NPWID=${encodeURIComponent(idForLink)}`;

    // ----------------------------
    // NEW BRANCH: Pending with CP Delegate => email ALL CP delegates
    // ----------------------------
    if (pendingWithCpDelegate) {
      if (!CP_EMAIL_ALLOWED) {
        toast.warn("CP email is disabled", {
          toastId: "cp-email-disabled",
          position: "top-right",
        });
        return;
      }
      const emails = await getCpDelegateEmails();
      if (!emails || emails.length === 0) {
        toast.error("No CP Delegate emails found to send reminder.", {
          toastId: "cp-email-empty",
          position: "top-right",
        });
        return;
      }

      const toList = emails.join(",");

      const payload = {
        subject: `Approval Reminder (CP Delegate) for NPWID : ${show(data?.npw_id || idForLink)}`,
        to: toList,
        cc: "",
        body: `<p>Hi Team,</p>
               <p>This request is pending with <b>CP Channel Delegate</b> approval. Please review:</p>
               <p><b>NPWID:</b> ${show(data?.npw_id || idForLink)}</p>
               <p>Thanks.</p>`,
        footer: "TeamBridge Notifications",
        link: linkPath,
        ctaLabel: "Open in TeamBridge",
        summary: {
          "Request ID": show(data?.npw_id || idForLink),
          Priority: show(data?.priority || "Normal"),
          Status: show(data?.status || "Pending"),
          "Pending With": "CP Delegate",
        },
      };

      const corrId = `ui-rem-cp-${Date.now()}`;

      try {
        setSending(true);
        await tbPostJson(EMAIL_NOTIFY_URL, payload, {
          /*headers: { "x-correlation-id": corrId } */
        });
        toast.success(`Reminder sent to CP Delegates (${emails.length})`, {
          toastId: "rem-sent-cp",
          position: "top-right",
        });
      } catch (e) {
        const msg = (e && (e.message || String(e))) || "Unknown error";
        console.error("Reminder API failed (CP reminder):", e);
        toast.error(`Failed to send reminder: ${msg}`, {
          toastId: "send-error-cp",
          position: "top-right",
        });
      } finally {
        setSending(false);
      }

      return;
      return;
    }

    // ----------------------------
    // EXISTING LOGIC: Pending with Employee Sponsor => keep as-is
    // ----------------------------
    const sponsorEmail = String(
      data?.sponsor_email || data?.sponsor || "",
    ).trim();
    if (!looksLikeEmail(sponsorEmail)) {
      toast.error("Sponsor email is missing/invalid. Cannot send reminder.", {
        toastId: "sponsor-email-invalid",
        position: "top-right",
      });
      return;
    }

    const payload = {
      subject: `Approval Reminder for NPWID : ${show(data?.npw_id || idForLink)}`,
      to: sponsorEmail,
      cc: "",
      body: `<p>Hi Team,</p><p>Please review request for NPWID : <b>${show(
        data?.npw_id || idForLink,
      )}</b> at the earliest.</p><p>Thanks.</p>`,
      footer: "TeamBridge Notifications",
      link: linkPath,
      ctaLabel: "Open in TeamBridge",
      summary: {
        "Request ID": show(data?.npw_id || idForLink),
        Priority: show(data?.priority || "Normal"),
        Status: show(data?.status || "Pending"),
      },
    };

    const corrId = `ui-rem-${Date.now()}`;

    try {
      setSending(true);
      await tbPostJson(EMAIL_NOTIFY_URL, payload, {
        /*headers: { "x-correlation-id": corrId } */
      });
      toast.success("Reminder sent", {
        toastId: "rem-sent",
        position: "top-right",
      });
    } catch (e) {
      const msg = (e && (e.message || String(e))) || "Unknown error";
      console.error("Reminder API failed (Sponsor reminder):", e);
      toast.error(`Failed to send reminder: ${msg}`, {
        toastId: "send-error",
        position: "top-right",
      });
    } finally {
      setSending(false);
    }
  }

  // ============================
  // FIXED: Back behavior
  //  - If opened from Search overlay => history back + set reopen flag
  //  - Else => go to returnPath (Manage Requests)
  // ============================
  const handleBack = () => {
    try {
      const fromSearch = sessionStorage.getItem(FROM_SEARCH_KEY) === "1";
      if (fromSearch) {
        sessionStorage.setItem(REOPEN_SEARCH_KEY, "1");
        navigate(-1);
        return;
      }

      const backTo =
        sessionStorage.getItem(RETURN_PATH_KEY) || "/manage-requests";
      navigate(backTo);
    } catch {
      navigate("/manage-requests");
    }
  };

  const comments = Array.isArray(data?.__comments_flat)
    ? data.__comments_flat
    : [];

  function Step({ title, lane, name, when, note, status }) {
    const p = status || pill(lane);
    return (
      <div className="ap-step" style={tint(lane)}>
        <div className="step-body">
          <div className="title">{title}</div>
          <span className={p.cls} style={{ marginBottom: 4 }}>
            {p.txt}
          </span>
          {note && (
            <div className="meta" style={{ color: "#6b7280" }}>
              {note}
            </div>
          )}
          <div className="meta break">{name || "—"}</div>
          <div className="meta">{when || ""}</div>
        </div>
      </div>
    );
  }

  const requestedBy =
    data?.submitter_name || data?.requested_by || data?.requested_name || "";
  const requestedOn =
    data?.requested_on || data?.submitted_on || data?.created_on || "";

  const rawDtvStatus = firstNonEmpty(
    data?.status,
    data?.dtvidStatus,
    data?.personStatus,
    data?.person_status,
  );
  const rawAttuidStatus = firstNonEmpty(
    data?.attuidStatus,
    data?.attuid_status,
    data?.__lookup_raw?.attuidStatus,
    data?.__lookup_raw?.attuid_status,
  );
  const dtvidStatusText = formatStatusLabel(rawDtvStatus) || "-";
  const attuidStatusText = formatStatusLabel(rawAttuidStatus, {
    notAssigned: true,
  });
  const attuidStatusKey = normalizeStatusKey(rawAttuidStatus);

  const showDtvIdRow = hasVal(data?.dtv_id);
  const showAttuidRow = hasVal(data?.attuid) && attuidStatusKey === "active";

  const statusKey = normalizeStatusKey(rawDtvStatus);
  const actorRoleLower = String(
    sessionStorage.getItem("tb_role") || "",
  ).toLowerCase();
  const isFteNominator = actorRoleLower.trim() === "fte nominator";
  const canOffboardBase = statusKey === "active";
  const hasAttuidForOffboard = Boolean(String(data?.attuid || data?.attUid || "").trim());
  const canOffboard =
    (isFteNominator ? canOffboardBase && hasAttuidForOffboard : canOffboardBase) &&
    !loading && !loadingDetailFails && !sending && !offboarding;
  const isFailedStatus = String(statusKey || "").includes("failed");
  const showReactivateAttuidButton =
    IS_REACTIVATE_ATTUID_NEEDED &&
    REACTIVE_ATTUID_VISIBLE_ROLES.some((r) => actorRoleLower.includes(r));
  const isInfoRequestedStatus =
    normalizeStatusKey(data?.status) === "inforequested";
  const canSubmitInfoReqToCp =
    isInfoRequestedStatus &&
    INFOREQ_SUBMIT_ALLOWED_ROLES.includes(actorRoleLower.trim());
  const infoReqCommentTrimmed = String(infoReqComment || "").trim();
  const dtvidStatusKey = normalizeStatusKey(rawDtvStatus);
  const canReactivateAttuid =
    dtvidStatusKey === "active" &&
    REACTIVE_ATTUID_STATUS_KEYS.includes(attuidStatusKey);
  const attuidPresentForWorker = hasVal(
    firstNonEmpty(
      data?.attuid,
      data?.attUid,
      data?.att_uid,
      data?.__lookup_raw?.attuid,
      data?.__lookup_raw?.attUid,
      data?.__lookup_raw?.att_uid,
    ),
  );
  const disableReactivateAttuid =
    sending ||
    reactivateSubmitting ||
    !canReactivateAttuid ||
    !attuidPresentForWorker;
  const reactivateDisableWhy =
    dtvidStatusKey !== "active"
      ? "Reactivate ATTUID is enabled only when DTVID status is Active."
      : !REACTIVE_ATTUID_STATUS_KEYS.includes(attuidStatusKey)
        ? "Reactivate ATTUID is enabled only for Inactive or Terminated ATTUID status."
        : !attuidPresentForWorker
          ? "There is no attuid for this worker, Please do the edit to have an attuid"
          : "";

  const reactivateAttuidButtonStyle = disableReactivateAttuid
    ? { cursor: "not-allowed" }
    : {
        background: "#008B8B",
        hover: "#1d4ed8",
        color: "#ffffff",
        borderColor: "#7c3aed",
        boxShadow: "0 8px 18px rgba(30,64,175,0.3)",
      };

  const workerName =
    `${show(data?.first_name)} ${show(data?.last_name)}`.trim() ||
    show(data?.full_name) ||
    show(data?.email);

  const openOffboardModal = () => {
    if (!canOffboard) {
      toast.error(
        isFteNominator && !hasAttuidForOffboard
          ? "Offboarding is disabled because ATTUID is missing for this worker."
          : "Offboarding is enabled only for Active workers.",
        { position: "top-right" },
      );
      return;
    }
    setOffboardDateError("");
    setOffboardError("");
    setOffboardNote("");
    setOffboardEndDate("");
    setOffboardIdType(isFteNominator ? "ATTUID" : "DTVID");
    setIsOffboardModalOpen(true);
  };

  const submitOffboard = async () => {
    if (!canOffboard) {
      setOffboardDateError("");
      setOffboardError("Offboarding is enabled only for Active workers.");
      return;
    }
    if (!offboardEndDate) {
      setOffboardDateError("Please select an end date.");
      setOffboardError("");
      return;
    }
    const today = new Date().toISOString().split("T")[0];
    if (new Date(offboardEndDate) < new Date(today)) {
      setOffboardDateError("Please select an end date greater than or equal to today.");
      setOffboardError("");
      return;
    }
    setOffboardDateError("");
    if (offboardIdType === "ATTUID" && !hasAttuidForOffboard) {
      setOffboardError("This worker does not have ATTUID.");
      return;
    }
    if (!OFFBOARD_URL) {
      setOffboardError("OFFBOARD_URL not configured");
      toast.error("OFFBOARD_URL not configured", {
        containerId: "global",
        position: "top-right",
      });
      return;
    }

    setOffboardError("");
    const actor = String(sessionStorage.getItem("tb_dtvid") || "").trim();
    const actorEmail = String(sessionStorage.getItem("tb_email") || "").trim();
    const actorName = String(sessionStorage.getItem("tb_name") || "").trim();

    try {
      setOffboarding(true);

      const common = {
        end_date: offboardEndDate,
        comments: offboardNote || "",
        performed_by_dtv_id: actor || "",
        performedName: actorName || actor || "",
        performed_at: new Date().toISOString(),
        name: workerName,
        actorEmail,
        submitter_id: actorEmail || undefined,
        submitter: actorEmail || undefined,
        submitter_email: actorEmail || undefined,
        submitter_name: actorName || actor || undefined,
        submitterDtvid: actor || undefined,
        submitter_dtvid: actor || undefined,
        rawActorEmail: actorEmail || undefined,
        action: "offboard",
      };

      const payload =
        String(firstNonEmpty(data?.npw_id, npwId) || "").trim() &&
        String(data?.security_id || "").trim()
          ? {
              ...common,
              npw_id: String(firstNonEmpty(data?.npw_id, npwId) || "").trim(),
              security_id: String(data?.security_id || "").trim(),
              offboardIdType,
              offboardByDtvId: offboardIdType === "DTVID",
              offboardByAttuid: offboardIdType === "ATTUID",
            }
          : {
              ...common,
              dtv_id:
                offboardIdType === "ATTUID"
                  ? String(data?.dtv_id || dtvId || "").trim()
                  : String(data?.dtv_id || dtvId || data?.email || "").trim(),
              offboardIdType,
              offboardByDtvId: offboardIdType === "DTVID",
              offboardByAttuid: offboardIdType === "ATTUID",
            };

      if (!payload.npw_id && !payload.dtv_id) {
        throw new Error("Missing NPWID/DTVID for offboarding.");
      }

      await tbPostJson(OFFBOARD_URL, payload, { fetchInit: { method: "POST" } });
      setIsOffboardModalOpen(false);
      toast.success("Off board request submitted.", {
        containerId: "global",
        position: "top-right",
      });
      setDetailsReloadTick((v) => v + 1);
    } catch (e) {
      const msg = extractOffboardError(e);
      setOffboardError(msg);
      toast.error(msg, { containerId: "global", position: "top-right" });
    } finally {
      setOffboarding(false);
    }
  };

  const dtvIdForReactivate = String(
    firstNonEmpty(
      dtvId,
      data?.dtv_id,
      data?.dtvId,
      data?.dtvAnchorId,
      data?.dtvid,
      data?.webphone_manager,
    ) || "",
  ).trim();
  const npwIdForReactivate = String(
    firstNonEmpty(npwId, data?.npw_id, data?.npwId, data?.NPWID) || "",
  ).trim();

  const lookupSource = reactivateLookup || data?.__lookup_raw || data || {};
  const lookupDetails = unwrapLookup(lookupSource) || lookupSource;
  const lookupCountry = firstNonEmpty(
    lookupDetails?.countryOfWorking,
    lookupDetails?.country,
    data?.country,
  );
  const reactivateIsUS = isUSCountry(lookupCountry);
  const reactivateSecLabel = reactivateIsUS ? "SSN" : "Security ID";
  const reactivateConfirmLabel = reactivateIsUS
    ? "Confirm SSN"
    : "Confirm Security ID";
  const reactivateLookupSecExist =
    reactivateSecExistValue || resolveIsSecExistValue(lookupSource);
  const reactivateLookupSecExistNormalized = normalizeIsSecExist(
    reactivateLookupSecExist,
  );
  const isReactivateSecExistYes = reactivateLookupSecExistNormalized === "yes";
  const reactivateSecurityLocked =
    isReactivateSecExistYes && !reactivateSecurityEditEnabled;
  const showSecurityEditButton =
    isReactivateSecExistYes && !reactivateSecurityEditEnabled;
  const showSecurityDisableEditButton =
    isReactivateSecExistYes && reactivateSecurityEditEnabled;

  const reactivateWorkerName = firstNonEmpty(
    `${show(lookupDetails?.firstName)} ${show(lookupDetails?.lastName)}`.trim(),
    `${show(data?.first_name)} ${show(data?.last_name)}`.trim(),
    data?.requested_by,
  );
  const reactivateWorkerDtvId = firstNonEmpty(
    lookupDetails?.dtvAnchorId,
    lookupDetails?.dtv_id,
    data?.dtv_id,
    dtvIdForReactivate,
  );
  const reactivateWorkerAttuid = firstNonEmpty(
    lookupDetails?.attuid,
    data?.attuid,
  );

  const missingCriticalFields = [
    [
      "dtvAnchorId",
      firstNonEmpty(
        lookupDetails?.dtvAnchorId,
        data?.dtv_id,
        dtvIdForReactivate,
      ),
    ],
    ["firstName", firstNonEmpty(lookupDetails?.firstName, data?.first_name)],
    ["lastName", firstNonEmpty(lookupDetails?.lastName, data?.last_name)],
    [
      "primaryEmail",
      firstNonEmpty(
        lookupDetails?.secondaryEmail,
        lookupDetails?.primaryEmail,
        data?.email,
      ),
    ],
    [
      "personType",
      firstNonEmpty(lookupDetails?.personType, data?.employment_type),
    ],
    [
      "countryOfWorking",
      firstNonEmpty(
        lookupDetails?.countryOfWorking,
        lookupDetails?.country,
        data?.country,
      ),
    ],
  ]
    .filter(([, v]) => !hasVal(v))
    .map(([k]) => k);

  const maxSecurityDigits = maxDigitsFor(reactivateIsUS);
  const normalizedSecurityDigits = extractDigitsByRegion(
    reactivateSecurityId,
    reactivateIsUS,
  );
  const normalizedConfirmDigits = extractDigitsByRegion(
    reactivateConfirmSecurityId,
    reactivateIsUS,
  );
  const reactivateSecurityInputValue = buildUnmaskedFormatted(
    reactivateIsUS,
    reactivateSecurityId,
  );
  const reactivateConfirmInputValue = buildUnmaskedFormatted(
    reactivateIsUS,
    reactivateConfirmSecurityId,
  );
  const reactivateSecurityMaskedValue = buildMaskedDisplay(
    reactivateIsUS,
    reactivateSecurityId,
  );
  const reactivateConfirmMaskedValue = buildMaskedDisplay(
    reactivateIsUS,
    reactivateConfirmSecurityId,
  );
  const reactivateErrors = getReactivateValidationErrors({
    securityDigits: normalizedSecurityDigits,
    confirmDigits: normalizedConfirmDigits,
    isUS: reactivateIsUS,
    securityLocked: reactivateSecurityLocked,
    securityLabel: reactivateSecLabel,
    confirmLabel: reactivateConfirmLabel,
  });
  const reactivateHasValidationErrors = Boolean(
    reactivateErrors.securityId || reactivateErrors.confirmSecurityId,
  );
  const reactivateSubmitValidationErrors = [];
  if (missingCriticalFields.length > 0) {
    reactivateSubmitValidationErrors.push(
      `Missing critical data: ${missingCriticalFields.join(", ")}. Please edit the worker data using Edit.`,
    );
  }
  if (!reactivateSecurityLocked) {
    const secErr = String(reactivateErrors.securityId || "").trim();
    const confirmErr = String(reactivateErrors.confirmSecurityId || "").trim();
    if (secErr) reactivateSubmitValidationErrors.push(secErr);
    if (confirmErr) reactivateSubmitValidationErrors.push(confirmErr);
  }
  // Hinge duplicate/security association is informational for Reactivate ATTUID.
  // Do not block CP validation or submit only because Hinge says the SSN/Security ID exists;
  // CP validate remains the source of truth for this reactivation flow.
  const reactivateSubmitValidationMessage =
    reactivateSubmitValidationErrors.find(Boolean) || "";
  const requiresReactivateCpValidation = IS_VALIDATE_CP_NEEDED;
  const reactivateValidationPassed = reactivateCpValidation.status === "passed";
  const reactivateHasBlockingErrors =
    Boolean(reactivateSubmitValidationMessage) ||
    (requiresReactivateCpValidation && !reactivateValidationPassed);
  const reactivateSubmitDisabled =
    reactivateSubmitting ||
    reactivateValidating ||
    reactivateLoading ||
    reactivateAssociation?.status === "checking" ||
    reactivateHasBlockingErrors;
  const reactivateSecurityFieldsIncomplete =
    !reactivateSecurityLocked &&
    (normalizedSecurityDigits.length !== maxSecurityDigits ||
      normalizedConfirmDigits.length !== maxSecurityDigits ||
      reactivateHasValidationErrors);
  const reactivateValidateDisabled =
    reactivateSubmitting ||
    reactivateValidating ||
    reactivateLoading ||
    reactivateAssociation?.status === "checking" ||
    reactivateSecurityFieldsIncomplete;

  useEffect(() => {
    if (!reactivateOpen) {
      if (reactivateAssocDebounceRef.current)
        clearTimeout(reactivateAssocDebounceRef.current);
      reactivateAssocDebounceRef.current = null;
      setReactivateAssociation({ status: "idle", message: "" });
      return;
    }
    if (!TB_HINGE_SEARCH_URL) {
      setReactivateAssociation({ status: "idle", message: "" });
      return;
    }
    if (reactivateSecurityLocked || reactivateLoading) {
      if (reactivateAssocDebounceRef.current)
        clearTimeout(reactivateAssocDebounceRef.current);
      reactivateAssocDebounceRef.current = null;
      setReactivateAssociation({ status: "idle", message: "" });
      return;
    }

    const primaryErr = String(reactivateErrors?.securityId || "").trim();
    const confirmErr = String(reactivateErrors?.confirmSecurityId || "").trim();
    const hasPrimaryError = Boolean(primaryErr);
    const hasAnySecurityError = hasPrimaryError || Boolean(confirmErr);

    const digits = extractDigitsByRegion(
      reactivateSecurityId || "",
      reactivateIsUS,
    );
    const min = Number.isFinite(HINGE_SSN_MIN_DIGITS)
      ? HINGE_SSN_MIN_DIGITS
      : 8;

    if (!digits || digits.length < min) {
      if (reactivateAssocDebounceRef.current)
        clearTimeout(reactivateAssocDebounceRef.current);
      reactivateAssocDebounceRef.current = null;
      setReactivateAssociation({ status: "idle", message: "" });
      return;
    }

    if (hasPrimaryError) {
      if (reactivateAssocDebounceRef.current)
        clearTimeout(reactivateAssocDebounceRef.current);
      reactivateAssocDebounceRef.current = null;
      setReactivateAssociation({ status: "idle", message: "" });
      return;
    }

    if (!reactivateIsUS) {
      const mmddMsg = validateSecurityIdMmdd8(digits);
      if (mmddMsg) {
        if (reactivateAssocDebounceRef.current)
          clearTimeout(reactivateAssocDebounceRef.current);
        reactivateAssocDebounceRef.current = null;
        setReactivateAssociation({ status: "idle", message: "" });
        return;
      }
    }

    if (reactivateAssocDebounceRef.current)
      clearTimeout(reactivateAssocDebounceRef.current);
    setReactivateAssociation((prev) =>
      prev?.status === "found"
        ? prev
        : { status: "checking", message: `Checking ${reactivateSecLabel}…` },
    );

    reactivateAssocDebounceRef.current = setTimeout(async () => {
      const seq = ++reactivateAssocSeqRef.current;
      try {
        const roleName = String(sessionStorage.getItem("tb_role") || "").trim();
        const supplierName = String(
          sessionStorage.getItem("tb_supplierName") ||
            sessionStorage.getItem("tb_vendorId") ||
            "",
        ).trim();
        const searchDtvId = String(
          sessionStorage.getItem("tb_dtvid") || "",
        ).trim();

        const params = new URLSearchParams();
        params.set("q", digits);
        params.set("isActive", "true");
        if (roleName) params.set("searcherRoleName", roleName);
        if (supplierName) params.set("searcherSupplierName", supplierName);
        if (searchDtvId) params.set("searchDtvId", searchDtvId);
        /*TODO:pentest*/ if (digits) params.set("searchMode", "validate");

        const url = `${TB_HINGE_SEARCH_URL}?${params.toString()}`;
        const resp = await tbGetJson(url);
        if (seq !== reactivateAssocSeqRef.current) return;

        const arr = normalizeHingePeople(resp?.response ?? resp);
        if (Array.isArray(arr) && arr.length > 0) {
          if (hasAnySecurityError) {
            setReactivateAssociation({ status: "idle", message: "" });
            return;
          }
          const foundName = hingePersonFullName(arr[0]);

          //Validate SSN / Security ID
          const loggedinDtvId = String(sessionStorage.getItem("tb_dtvid") || "").trim().toLowerCase();
          const foundDtvid = String(
            arr[0]?.dtvAnchorId || arr[0]?.dtvId || arr[0]?.dtv_id || "",
          ).trim().toLowerCase();
          const isloggedinDtvId = Boolean(loggedinDtvId) && loggedinDtvId === foundDtvid;
          const editingDtvId =  reactivateWorkerDtvId;
          const isEditingDtvId =
            Boolean(editingDtvId) &&
            String(editingDtvId).trim().toLowerCase() === foundDtvid;

          //For the case when the security ID is already associated with the person being edited
          if (isEditingDtvId) {
            const isEditingLoggedInDtvId = isEditingDtvId && isloggedinDtvId;
            setReactivateAssociation({
              status: "found",
              message: isEditingLoggedInDtvId
                ? `${reactivateSecLabel} is already associated with you.`
                : `${reactivateSecLabel} is already associated with the same person which you are trying to update.`
            });
          }
          //For the case when the security ID is associated with logged in user or someone else
          else {
            setReactivateAssociation({
              status: "found",
              message: isloggedinDtvId
                ? `${reactivateSecLabel} is already associated with you.`
                : `${reactivateSecLabel} is already associated to ${foundName || "an existing person"}.`,
            });
          }
        } else {
          if (!reactivateIsUS) {
            const mmddMsg = validateSecurityIdMmdd8(digits);
            if (mmddMsg) {
              setReactivateAssociation({ status: "idle", message: "" });
              return;
            }
          }
          if (hasAnySecurityError) {
            setReactivateAssociation({ status: "idle", message: "" });
            return;
          }
          setReactivateAssociation({
            status: "none",
            message: reactivateIsUS
              ? "SSN is available to use."
              : "Security ID is available.",
          });
        }
      } catch {
        if (seq !== reactivateAssocSeqRef.current) return;
        setReactivateAssociation({ status: "idle", message: "" });
      }
    }, HINGE_SSN_DEBOUNCE_MS);

    return () => {
      if (reactivateAssocDebounceRef.current)
        clearTimeout(reactivateAssocDebounceRef.current);
      reactivateAssocDebounceRef.current = null;
    };
  }, [
    reactivateOpen,
    reactivateSecurityId,
    reactivateErrors?.securityId,
    reactivateErrors?.confirmSecurityId,
    reactivateIsUS,
    reactivateSecurityLocked,
    reactivateLoading,
    reactivateSecLabel,
  ]);

  const openEditPage = ({ reactiveAttuid = false } = {}) => {
    const from =
      `${window.location.pathname || ""}${window.location.search || ""}${window.location.hash || ""}` ||
      "/";
    const navState = reactiveAttuid
      ? { from, reactivateAttuid: true }
      : { from };
    const qsSuffix = reactiveAttuid ? "?reactivate_attuid=1" : "";
    navigate(
      `/onboarding/new/${encodeURIComponent(npwId || rawParamId)}${qsSuffix}`,
      { state: navState },
    );
  };

  const resetReactivateForm = () => {
    reactivateValidateSeqRef.current += 1;
    setReactivateLookup(null);
    setReactivateShowSecurity(false);
    setReactivateShowConfirmSecurity(false);
    setReactivateSecurityEditEnabled(false);
    setReactivateValidating(false);
    setReactivateWebphoneValidation({ status: "idle", message: "" });
    setReactivateWebphoneManager({
      managerId: "",
      webphoneManagerAttuid: null,
      webphoneManagerEmail: "",
    });
    setReactivateCpValidation({ status: "idle", message: "" });
  };

  const applyReactivateSource = (source) => {
    const full = source && typeof source === "object" ? source : {};
    const inner = unwrapLookup(full) || full;
    setReactivateLookup(full);
    setReactivateSecExistValue(resolveIsSecExistValue(full));

    const sourceCountry = firstNonEmpty(
      inner?.countryOfWorking,
      inner?.country,
      data?.country,
    );
    const isUSForInitial = isUSCountry(sourceCountry);
    const secDigits = resolveSecurityDigits(inner, isUSForInitial);
    setReactivateInitialSecurityDigits(secDigits);
    setReactivateSecurityId(secDigits);
    setReactivateConfirmSecurityId(secDigits);
    setReactivateSecurityEditEnabled(false);
    setReactivateWebphoneValidation({ status: "idle", message: "" });
    setReactivateWebphoneManager({
      managerId: "",
      webphoneManagerAttuid: null,
      webphoneManagerEmail: "",
    });
    setReactivateCpValidation({ status: "idle", message: "" });
  };

  const loadReactivateLookup = async () => {
    const base =
      TB_HINGE_PERSON_LOOKUP_URL ||
      ENV.LOOKUP_URL ||
      (ENV.HINGE_BASE
        ? `${ENV.HINGE_BASE.replace(/\/$/, "")}/person/lookup`
        : "");
    if (!base) {
      toast.error("Hinge/TB-Hinge lookup URL is not configured.", {
        toastId: "react-hinge-env",
        position: "top-right",
      });
      return null;
    }
    if (!dtvIdForReactivate) {
      toast.error("Missing DTVID for reactivation lookup.", {
        toastId: "react-dtv-missing",
        position: "top-right",
      });
      return null;
    }

    const url = `${base}${base.includes("?") ? "&" : "?"}dtvId=${encodeURIComponent(dtvIdForReactivate)}&_=${Date.now()}`;
    try {
      setReactivateLoading(true);
      const payload = await tbGetJson(url);
      applyReactivateSource(payload);
      return payload;
    } catch (e) {
      console.error("Reactivation lookup failed", e);
      toast.error("Failed to load worker details from lookup API.", {
        toastId: "react-lookup-failed",
        position: "top-right",
      });
      return null;
    } finally {
      setReactivateLoading(false);
    }
  };

  const onReactivateSecurityChange = (nextRaw, target) => {
    const nextDigits = extractDigitsByRegion(nextRaw, reactivateIsUS);
    if (target === "security") setReactivateSecurityId(nextDigits);
    else setReactivateConfirmSecurityId(nextDigits);
    setReactivateCpValidation((prev) =>
      prev.status === "idle" ? prev : { status: "idle", message: "" },
    );
  };

  const handleEnableReactivateSecurityEdit = () => {
    setReactivateSecurityEditEnabled(true);
    setReactivateCpValidation((prev) =>
      prev.status === "idle" ? prev : { status: "idle", message: "" },
    );
  };

  const handleDisableReactivateSecurityEdit = () => {
    setReactivateSecurityEditEnabled(false);
    setReactivateShowSecurity(false);
    setReactivateShowConfirmSecurity(false);
    setReactivateSecurityId(reactivateInitialSecurityDigits);
    setReactivateConfirmSecurityId(reactivateInitialSecurityDigits);
    setReactivateCpValidation((prev) =>
      prev.status === "idle" ? prev : { status: "idle", message: "" },
    );
  };

  const validateReactivateAttuid = async () => {
    const validateSeq = ++reactivateValidateSeqRef.current;
    const isStaleValidate = () =>
      validateSeq !== reactivateValidateSeqRef.current;

    if (!CP_VALIDATE_URL) {
      toast.error("CP validate endpoint is not configured.", {
        toastId: "react-cp-validate-url",
        position: "top-right",
      });
      return;
    }
    if (!requiresReactivateCpValidation) {
      setReactivateCpValidation({
        status: "passed",
        message: "Validation not required",
      });
      return;
    }

    const npwNumeric = /^\d+$/.test(String(npwIdForReactivate || "").trim())
      ? String(npwIdForReactivate).trim()
      : null;
    const source = reactivateLookup || data?.__lookup_raw || {};
    const sourceDetails = unwrapLookup(source) || source;
    const typedSecurityDigits = extractDigitsByRegion(
      reactivateSecurityId,
      reactivateIsUS,
    );
    const typedConfirmDigits = extractDigitsByRegion(
      reactivateConfirmSecurityId,
      reactivateIsUS,
    );
    const hasTypedSecurity =
      !!typedSecurityDigits &&
      (reactivateSecurityEditEnabled ||
        typedSecurityDigits !== String(reactivateInitialSecurityDigits || "") ||
        typedConfirmDigits !== String(reactivateInitialSecurityDigits || ""));
    const ssnSecId = hasTypedSecurity
      ? typedSecurityDigits || typedConfirmDigits
      : "";

    if (reactivateSubmitValidationMessage) {
      toast.error(reactivateSubmitValidationMessage, {
        toastId: "reactivate-validation",
        position: "top-right",
      });
      setReactivateCpValidation({
        status: "failed",
        message: reactivateSubmitValidationMessage,
      });
      return;
    }

    const cpProfileId = firstNonEmpty(
      sourceDetails?.attRoleCpProfileId,
      sourceDetails?.att_role_cp_profile_id,
      data?.att_role_cp_profile_id,
      sourceDetails?.cinglepointProfileId,
      sourceDetails?.singlepointProfileId,
    );
    if (!cpProfileId) {
      const msg =
        "Missing mandatory CP Profile ID for Reactivate ATTUID , Please use Edit button flow.";
      toast.error(msg, {
        toastId: "reactivate-cp-profile-missing",
        position: "top-right",
      });
      setReactivateCpValidation({ status: "failed", message: msg });
      return;
    }

    let reactivateWebphone = {
      managerId: getReactivateManagerId(sourceDetails, data),
      webphoneManagerAttuid: null,
      webphoneManagerEmail: "",
      status: "idle",
      message: "",
      response: null,
    };
    try {
      setReactivateWebphoneValidation({
        status: "checking",
        message: "Validating Webphone Manager...",
      });
      reactivateWebphone = await validateReactivateWebphoneManager(
        sourceDetails,
        data,
      );
      if (isStaleValidate()) return;
      setReactivateWebphoneManager({
        managerId: reactivateWebphone.managerId || "",
        webphoneManagerAttuid: reactivateWebphone.webphoneManagerAttuid || null,
        webphoneManagerEmail: reactivateWebphone.webphoneManagerEmail || "",
      });
      setReactivateWebphoneValidation({
        status: reactivateWebphone.status || "idle",
        message: reactivateWebphone.message || "",
      });
    } catch (e) {
      if (isStaleValidate()) return;
      const managerId = getReactivateManagerId(sourceDetails, data);
      reactivateWebphone = {
        managerId,
        webphoneManagerAttuid: null,
        webphoneManagerEmail: "",
        status: "error",
        message: "Webphone Manager validation failed",
        response: null,
      };
      setReactivateWebphoneManager({
        managerId,
        webphoneManagerAttuid: null,
        webphoneManagerEmail: "",
      });
      setReactivateWebphoneValidation({
        status: "error",
        message: "Webphone Manager validation failed",
      });
    }
    const reactivateWebphoneManagerAttuid =
      reactivateWebphone.webphoneManagerAttuid || null;

    const requestDetail = {
      cp_approver_attuid: firstNonEmpty(
        data?.approver_attuid,
        sourceDetails?.approverAttuid,
        sourceDetails?.approverDtvId,
        data?.approver_id,
        sessionStorage.getItem("tb_attuid"),
      ),
      first_name: firstNonEmpty(sourceDetails?.firstName, data?.first_name),
      last_name: firstNonEmpty(sourceDetails?.lastName, data?.last_name),
      email: firstNonEmpty(
        sourceDetails?.secondaryEmail,
        sourceDetails?.primaryEmail,
        data?.email,
      ),
      phone: firstNonEmpty(
        sourceDetails?.phone,
        data?.phone,
        data?.phone_number,
      ),
      phone_number: firstNonEmpty(
        sourceDetails?.phone,
        data?.phone,
        data?.phone_number,
      ),
      country: firstNonEmpty(
        sourceDetails?.countryOfCitizenship,
        sourceDetails?.countryOfWorking,
        sourceDetails?.country,
        data?.country,
      ),
      countryOfWorking: firstNonEmpty(
        sourceDetails?.countryOfWorking,
        sourceDetails?.country,
        data?.country,
      ),
      attuid: firstNonEmpty(sourceDetails?.attuid, data?.attuid),
      dtvid: firstNonEmpty(
        sourceDetails?.dtvAnchorId,
        data?.dtv_id,
        dtvIdForReactivate,
      ),
      worker_type_tax: firstNonEmpty(
        sourceDetails?.workerTypeTax,
        sourceDetails?.w2_1099,
        data?.w2_1099,
      ),
      webphone_manager_attuid: reactivateWebphoneManagerAttuid,
      cp_role_id: firstNonEmpty(
        sourceDetails?.attRoleCpRoleId,
        sourceDetails?.att_role_cp_role_id,
        data?.att_role_cp_role_id,
      ),
      role: firstNonEmpty(sourceDetails?.dtvRoleName, data?.role),
      cp_profile_id: cpProfileId,
      sow: cpProfileId,
      security_id_hash:
        firstNonEmpty(
          sourceDetails?.security_id_hash,
          sourceDetails?.securityIdHash,
        ) || null,
      security_id_plaintext: null,
      securityId: null,
      security_id: null,
      ssn: null,
      ssn_plain: null,
      secid: null,
      sec_id: null,
      start_date: firstNonEmpty(
        sourceDetails?.effectiveStartDate,
        data?.start_date,
        new Date().toISOString().slice(0, 10),
      ),
      end_date:
        firstNonEmpty(sourceDetails?.effectiveEndDate, data?.end_date) || null,
      comments: "Validation from UI no-npw flow",
      fte_nominated: false,
      offboard_by_attuid: false,
      ...(ssnSecId
        ? {
            ssnSecId,
            ssn: ssnSecId,
            security_id: ssnSecId,
            securityId: ssnSecId,
            security_id_plaintext: ssnSecId,
            ssn_plain: ssnSecId,
          }
        : {}),
    };

    const payload = {
      req_type: "action",
      action: "VALIDATE",
      client_request_id:
        clientNewReqId?.() ||
        `ui-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      request_detail: requestDetail,
      ...(npwNumeric ? { npw_id: Number(npwNumeric) } : {}),
    };

    try {
      setReactivateValidating(true);

      setReactivateCpValidation({
        status: "checking",
        message: "Validating with AT&T , Please Wait...!",
      });

      const resp = await tbPostJson(CP_VALIDATE_URL, payload, {
        fetchInit: { method: "POST" },
      });
      if (isStaleValidate()) return;

      const failed = hasApiValidationFailure(resp);
      const messages = getCpErrorMessages(resp, { max: 12 });

      if (failed) {
        console.error(
          "%c[REACTIVATE] Cinglepoint validation FAILED",
          "color: red; font-weight: bold;",
        );

        console.log("[REACTIVATE] Cinglepoint response:", resp);

        console.log("[REACTIVATE] Cinglepoint results:", resp?.results);

        console.log("[REACTIVATE] Cinglepoint messages:", messages);

        // ---------------------------------------------
        // CP FAILED → CHECK ROLE CATALOG
        // ---------------------------------------------

        let isValidRole = true;

        try {
          console.log(
            "%c[ROLE CATALOG] CP failed. Calling Role Catalog...",
            "color: orange; font-weight: bold;",
          );

          const roleCatalogResponse = await tbGetJson(HINGE_ROLE_LIST_URL);

          if (isStaleValidate()) return;

          console.log("[ROLE CATALOG] Response:", roleCatalogResponse);

          isValidRole = validateRoleCatalogMatch(
            sourceDetails,
            roleCatalogResponse,
          );

          console.log(
            "%c[ROLE CATALOG] FINAL RESULT:",
            "color: blue; font-weight: bold;",
            isValidRole,
          );
        } catch (roleCatalogError) {
          if (isStaleValidate()) return;

          console.error("[ROLE CATALOG] API ERROR:", roleCatalogError);

          // Role Catalog API failure should NOT mean invalid role
          isValidRole = true;
        }

        // ---------------------------------------------
        // 1. SHOW CINGLEPOINT ERROR
        // ---------------------------------------------

        if (messages.length) {
          messages.forEach((msg) => {
            toast.error(`${msg}`, {
              position: "top-right",
            });
          });
        } else {
          toast.error("CP validation failed.", {
            toastId: "reactivate-cp-validation-failed",
            position: "top-right",
          });
        }

        // ---------------------------------------------
        // 2. SHOW ROLE CATALOG ERROR IF NOT MATCHED
        // ---------------------------------------------

        if (!isValidRole) {
          const invalidRoleMsg =
            "Invalid role, Please use the edit to change the role and resubmit.";

          console.error(
            "%c[ROLE CATALOG] INVALID ROLE",
            "color: red; font-weight: bold;",
          );

          toast.error(invalidRoleMsg, {
            toastId: "reactivate-invalid-role-helper",
            position: "top-right",
          });

          console.log("[ROLE CATALOG] Invalid role message displayed");
        }

        // ---------------------------------------------
        // VALIDATION FAILED
        // ---------------------------------------------

        setReactivateCpValidation({
          status: "failed",
          message: messages[0] || "CP validation failed",
        });

        return;
      }

      setReactivateCpValidation({
        status: "passed",
        message: "Validation successful",
      });

      toast.success("Validation successful. You can submit now.", {
        toastId: "reactivate-validate-success",
        position: "top-right",
      });
    } catch (e) {
      if (isStaleValidate()) return;
      const messages = getCpErrorMessages(e, { max: 12 });
      const msg =
        messages[0] || String(e?.message || e || "Validation failed").trim();
      setReactivateCpValidation({ status: "failed", message: msg });
      if (messages.length) {
        messages.forEach((item) =>
          toast.error(item, { position: "top-right" }),
        );
      } else {
        toast.error(msg || "CP validation failed.", {
          toastId: "reactivate-validate-failed",
          position: "top-right",
        });
      }
    } finally {
      if (!isStaleValidate()) setReactivateValidating(false);
    }
  };

  const submitReactivateAttuid = async () => {
    if (!ONBOARDING_NEW_URL) {
      toast.error("Onboarding new endpoint is not configured.", {
        toastId: "react-onboard-missing",
        position: "top-right",
      });
      return;
    }
    if (reactivateSubmitDisabled) return;
    if (reactivateSubmitValidationMessage) {
      toast.error(reactivateSubmitValidationMessage, {
        toastId: "reactivate-validation",
        position: "top-right",
      });
      return;
    }
    if (
      requiresReactivateCpValidation &&
      reactivateCpValidation.status !== "passed"
    ) {
      toast.error(
        "Please click Validate and resolve all CP errors before submitting.",
        {
          toastId: "reactivate-validate-first",
          position: "top-right",
        },
      );
      return;
    }

    const ok = window.confirm(
      `Are you sure you want to reactivate ${reactivateWorkerName || "this worker"}?`,
    );
    if (!ok) return;

    const source = reactivateLookup || data?.__lookup_raw || {};
    const sourceDetails = unwrapLookup(source) || source;
    const secDigits = reactivateSecurityLocked
      ? ""
      : extractDigitsByRegion(reactivateSecurityId, reactivateIsUS);
    const npwNumeric = /^\d+$/.test(String(npwIdForReactivate || "").trim())
      ? String(npwIdForReactivate).trim()
      : null;
    const actorEmail = String(sessionStorage.getItem("tb_email") || "").trim();
    const actorNameLocal = String(
      sessionStorage.getItem("tb_name") || "",
    ).trim();
    const actorDtvId = String(sessionStorage.getItem("tb_dtvid") || "").trim();
    //pentest code  //pentest code fencode
    const actorRoleRaw = String(sessionStorage.getItem("tb_role") || "").trim();
    const ts = Date.now();
    const vRaw = `${actorRoleRaw}_*v*_${ts}`;
    const dRaw = `${actorDtvId}_*v*_${ts}`;
    const vParam = typeof btoa === "function" ? btoa(vRaw) : window.btoa(vRaw);
    const dParam = typeof btoa === "function" ? btoa(dRaw) : window.btoa(dRaw);

    const actorNominatorId = firstNonEmpty(actorDtvId, actorEmail);
    const actorNominatorEmail = firstNonEmpty(
      actorEmail,
      data?.submitter_id,
      sourceDetails?.nominatorEmailId,
    );
    const actorNominatorName = firstNonEmpty(
      actorNameLocal,
      data?.submitter_name,
      sourceDetails?.nominatorName,
    );
    const sourceSubmitterId = firstNonEmpty(
      actorNominatorEmail,
      actorNominatorId,
      data?.submitter_id,
    );
    const sourceApproverId = firstNonEmpty(
      data?.approver_id,
      sourceDetails?.approverEmailId,
      sourceDetails?.approverDtvId,
      actorEmail,
      actorDtvId,
    );
    const sourceSubmitterName = actorNominatorName;
    const roleSess = firstNonEmpty(
      sessionStorage.getItem("tb_role"),
      "Nominator",
    );
    const webphoneManagerId = firstNonEmpty(
      reactivateWebphoneManager?.managerId,
      getReactivateManagerId(sourceDetails, data),
    );
    const webphoneManagerAttuid =
      reactivateWebphoneManager?.webphoneManagerAttuid || null;
    const webphoneManagerEmail =
      reactivateWebphoneManager?.webphoneManagerEmail || "";
    const form1 = {
      attuid: firstNonEmpty(sourceDetails?.attuid, data?.attuid),
      attuidRequired: "Yes",
      site: firstNonEmpty(sourceDetails?.site),
      location: firstNonEmpty(sourceDetails?.location),
      firstName: firstNonEmpty(sourceDetails?.firstName, data?.first_name),
      lastName: firstNonEmpty(sourceDetails?.lastName, data?.last_name),
      phone: firstNonEmpty(sourceDetails?.phone, data?.phone),
      role: firstNonEmpty(sourceDetails?.dtvRoleName, data?.role),
      startDate: firstNonEmpty(
        sourceDetails?.effectiveStartDate,
        data?.start_date,
      ),
      endDate: firstNonEmpty(sourceDetails?.effectiveEndDate, data?.end_date),
      autoRegister: firstNonEmpty(sourceDetails?.autoRegister, "Yes"),
      timeZone: firstNonEmpty(sourceDetails?.timeZone, data?.time_zone),
      department: firstNonEmpty(sourceDetails?.department, "DIRECT (EGA003)"),
      webphoneManagerId: webphoneManagerId,
      webphone_manager_attuid: webphoneManagerAttuid,
      webphoneManagerEmail: webphoneManagerEmail,
      webphone_manager_email: webphoneManagerEmail,
      npwType: firstNonEmpty(
        sourceDetails?.personType,
        sourceDetails?.person_type,
        data?.employment_type,
        sourceDetails?.npwType,
        "Non-staff Supp Worker",
      ),
      workstation: firstNonEmpty(sourceDetails?.workstation),
      occupancyCode: firstNonEmpty(
        sourceDetails?.occupancyIndicator,
        sourceDetails?.occupancy,
        data?.occupancy,
      ),
      workerPrimaryContact: firstNonEmpty(sourceDetails?.workerPrimaryContact),
      nonBillable: firstNonEmpty(
        sourceDetails?.nonBillableIndicator,
        sourceDetails?.nonBillable,
        data?.non_billable,
      ),
      sponsor: firstNonEmpty(sourceDetails?.employeeSponsor, data?.sponsor),
      sponsorEmail: firstNonEmpty(
        sourceDetails?.approverEmailId,
        data?.sponsor_email,
      ),
      sponsor_email: firstNonEmpty(
        sourceDetails?.approverEmailId,
        data?.sponsor_email,
      ),
      contractPo: firstNonEmpty(
        sourceDetails?.contractNumber,
        data?.contract,
        data?.contract_po,
      ),
      billRate: firstNonEmpty(sourceDetails?.billRate),
      boltApproval: firstNonEmpty(sourceDetails?.boltApproval),
      npwManagerCompany: firstNonEmpty(
        sourceDetails?.supplierName,
        data?.vendor,
      ),
      workerTypeTax: firstNonEmpty(
        sourceDetails?.workerTypeTax,
        sourceDetails?.w2_1099,
      ),
      citizenship: firstNonEmpty(
        sourceDetails?.countryOfCitizenship,
        data?.citizenship,
      ),
      country: firstNonEmpty(
        sourceDetails?.countryOfCitizenship,
        sourceDetails?.countryOfWorking,
        sourceDetails?.country,
        data?.country,
      ),
      countryOfWorking: firstNonEmpty(
        sourceDetails?.countryOfWorking,
        sourceDetails?.country,
        data?.country,
      ),
      city: firstNonEmpty(
        sourceDetails?.city,
        sourceDetails?.workCity,
        data?.city,
      ),
      state: firstNonEmpty(
        sourceDetails?.state,
        sourceDetails?.workState,
        data?.state,
      ),
      autoInvoiceAll: firstNonEmpty(sourceDetails?.autoInvoiceAll, "No"),
      supplierReference: firstNonEmpty(
        sourceDetails?.supplierReference,
        sourceDetails?.contractNumber,
        data?.supplier_reference,
      ),
      username: firstNonEmpty(sourceDetails?.username),
      displayWorkforceToBuyer: firstNonEmpty(
        sourceDetails?.displayWorkforceToBuyer,
      ),
      timeSheetType: firstNonEmpty(
        sourceDetails?.timeSheetType,
        "No Time Sheets",
      ),
      vendor: firstNonEmpty(sourceDetails?.supplierName, data?.vendor),
      dtvId: firstNonEmpty(
        sourceDetails?.dtvAnchorId,
        data?.dtv_id,
        dtvIdForReactivate,
      ),
      poNum: firstNonEmpty(
        sourceDetails?.poNumber,
        data?.po_number,
        data?.po_num,
      ),
      lineNum: firstNonEmpty(sourceDetails?.lineNumber, data?.line_number),
      email: firstNonEmpty(
        sourceDetails?.secondaryEmail,
        sourceDetails?.primaryEmail,
        data?.email,
      ),
      attRoleKey: firstNonEmpty(
        sourceDetails?.attRoleKey,
        sourceDetails?.attRoleCpRoleId,
        sourceDetails?.att_role_cp_role_id,
        data?.att_role_cp_role_id,
        sourceDetails?.cinglepointRoleId,
        sourceDetails?.singlepointRoleId,
      ),
      attRoleDtvRoleId: firstNonEmpty(sourceDetails?.attRoleDtvRoleId),
      attRoleDtvRoleName: firstNonEmpty(sourceDetails?.attRoleDtvRoleName),
      attRoleCpRoleId: firstNonEmpty(
        sourceDetails?.attRoleCpRoleId,
        sourceDetails?.att_role_cp_role_id,
        data?.att_role_cp_role_id,
        sourceDetails?.cinglepointRoleId,
        sourceDetails?.singlepointRoleId,
      ),
      attRoleCpRoleDesc: firstNonEmpty(
        sourceDetails?.attRoleCpRoleDesc,
        sourceDetails?.att_role_cp_role_desc,
        data?.att_role_cp_role_desc,
        sourceDetails?.cinglepointRoleDesc,
        sourceDetails?.cinglepointRoleId,
        sourceDetails?.singlepointRoleId,
      ),
      attRoleCpProfileId: firstNonEmpty(
        sourceDetails?.attRoleCpProfileId,
        sourceDetails?.att_role_cp_profile_id,
        data?.att_role_cp_profile_id,
        sourceDetails?.cinglepointProfileId,
        sourceDetails?.singlepointProfileId,
      ),
      attRoleCpProfileDesc: firstNonEmpty(
        sourceDetails?.attRoleCpProfileDesc,
        sourceDetails?.att_role_cp_profile_desc,
        data?.att_role_cp_profile_desc,
        sourceDetails?.cinglepointProfileDesc,
        sourceDetails?.cinglepointProfileId,
        sourceDetails?.singlepointProfileId,
      ),
      isAttuidRequested: "Yes",
      attuid_requested: "Yes",
      isSecExist: reactivateLookupSecExist || "",
    };
    if (hasVal(firstNonEmpty(sourceDetails?.attuid, data?.attuid)))
      form1.attuid = firstNonEmpty(sourceDetails?.attuid, data?.attuid);
    if (secDigits) {
      form1.securityId = secDigits;
      form1.security_id = secDigits;
      form1.ssn = secDigits;
      form1.ssnSecId = secDigits;
    }

    const payload = {
      workerType: "Non-Payroll Worker",
      form1,
      status: "Submitted",
      buttonAction: "Submitted",
      webphone_manager_attuid: webphoneManagerAttuid,
      submitter: sourceSubmitterId,
      submitter_id: sourceSubmitterId,
      loggedInUserEmail: sourceSubmitterId,
      submitterDtvid: actorDtvId || undefined,
      submitterName: sourceSubmitterName,
      submitter_name: sourceSubmitterName,
      loggedInUserName: sourceSubmitterName,
      nominatorId: actorNominatorId || undefined,
      nominator_id: actorNominatorId || undefined,
      nominatorDtvId: actorDtvId || actorNominatorId || undefined,
      nominator_dtvid: actorDtvId || actorNominatorId || undefined,
      nominatorEmailId: actorNominatorEmail || undefined,
      nominator_email_id: actorNominatorEmail || undefined,
      nominatorName: actorNominatorName || undefined,
      nominator_name: actorNominatorName || undefined,
      requested_by: actorNominatorName || undefined,
      approver: sourceApproverId,
      loggedInUserRole: roleSess,
      nominatorRole: roleSess,
      attuid: form1.attuid || "",
      reactivate_attuid: "true",
      reactive_attuid: true,
      npw_id: npwNumeric,
      npwId: npwNumeric,
      //  //pentest code fencode
      v: vParam,
      d: dParam,

      ...(secDigits
        ? {
            securityId: secDigits,
            security_id: secDigits,
            ssn: secDigits,
            ssnSecId: secDigits,
          }
        : {}),
    };

    try {
      setReactivateSubmitting(true);
      await tbPostJson(ONBOARDING_NEW_URL, payload);
      toast.success("Request submit successfully", {
        toastId: "reactivate-success",
        position: "top-right",
      });
      setReactivateOpen(false);
      resetReactivateForm();
    } catch (e) {
      const messages = getCpErrorMessages(e, { max: 12 });
      const msg =
        messages[0] || (e && (e.message || String(e))) || "Unknown error";
      console.error("Reactivate ATTUID submit failed", e);
      if (messages.length) {
        messages.forEach((item) =>
          toast.error(item, { position: "top-right" }),
        );
      } else {
        toast.error(`Request submit failed: ${msg}`, {
          toastId: "reactivate-failed",
          position: "top-right",
        });
      }
    } finally {
      setReactivateSubmitting(false);
    }
  };

  const onReactivateAttuidClick = () => {
    if (disableReactivateAttuid) return;
    const cachedSource = data?.__lookup_raw || data || {};
    const hasCachedLookupData =
      hasVal(firstNonEmpty(cachedSource?.firstName, data?.first_name)) &&
      hasVal(firstNonEmpty(cachedSource?.lastName, data?.last_name)) &&
      hasVal(
        firstNonEmpty(
          cachedSource?.dtvAnchorId,
          cachedSource?.dtv_id,
          data?.dtv_id,
          dtvIdForReactivate,
        ),
      );
    const cachedSecExist = resolveIsSecExistValue(cachedSource);
    const hasCachedSecExist =
      cachedSecExist === "yes" || cachedSecExist === "no";

    resetReactivateForm();
    setReactivateSecExistValue(hasCachedSecExist ? cachedSecExist : "");
    setReactivateCpValidation({ status: "idle", message: "" });
    setReactivateOpen(true);
    if (hasCachedLookupData && hasCachedSecExist) {
      applyReactivateSource(cachedSource);
      return;
    }

    loadReactivateLookup();
  };

  const submitInfoRequestedToCp = async () => {
    if (!APPROVAL_ACTION_URL) {
      toast.error("Approval action endpoint is not configured.", {
        toastId: "inforeq-action-url",
        position: "top-right",
      });
      return;
    }
    if (!canSubmitInfoReqToCp) return;
    if (!infoReqCommentTrimmed) {
      toast.error("Comment is required before submit.", {
        toastId: "inforeq-comment-required",
        position: "top-right",
      });
      return;
    }
    if (infoReqCommentTrimmed.length > INFOREQ_COMMENT_MAX_LENGTH) {
      toast.error(
        `Comment cannot exceed ${INFOREQ_COMMENT_MAX_LENGTH} characters.`,
        {
          toastId: "inforeq-comment-maxlen",
          position: "top-right",
        },
      );
      return;
    }

    const actorEmail = String(sessionStorage.getItem("tb_email") || "").trim();
    const actorName = String(sessionStorage.getItem("tb_name") || "").trim();
    const actorRole = String(sessionStorage.getItem("tb_role") || "").trim();
    //pentest code fencode
    const actorDtvId = String(sessionStorage.getItem("tb_dtvid") || "").trim();
    const ts = Date.now();
    const vRaw = `${actorRole}_*v*_${ts}`;
    const dRaw = `${actorDtvId}_*v*_${ts}`;
    const nRaw = `${npwId || rawParamId}_*v*_${ts}`;
    const vParam = typeof btoa === "function" ? btoa(vRaw) : window.btoa(vRaw);
    const dParam = typeof btoa === "function" ? btoa(dRaw) : window.btoa(dRaw);
    const nParam = typeof btoa === "function" ? btoa(nRaw) : window.btoa(nRaw);
    const payload = {
      npw_id: String(
        firstNonEmpty(data?.npw_id, npwId, rawParamId) || "",
      ).trim(),
      action: "inforeq_submit_tocp",
      actor_email: actorEmail,
      actor_name: actorName,
      actor_role: actorRole,
      comment_block: infoReqCommentTrimmed,
      //  //pentest code fencode
      v: vParam,
      d: dParam,
      n: nParam,
    };

    try {
      setInfoReqSubmitting(true);
      await tbPostJson(APPROVAL_ACTION_URL, payload);
      toast.success("Submitted Successfully.", {
        toastId: "inforeq-submit-success",
        position: "top-right",
      });
      setInfoReqComment("");
      setTimeout(() => {
        setDetailsReloadTick((v) => v + 1);
      }, INFOREQ_SUCCESS_RELOAD_DELAY_MS);
    } catch (e) {
      const msg = (e && (e.message || String(e))) || "Unknown error";
      toast.error(`Failed to submit comment: ${msg}`, {
        toastId: "inforeq-submit-failed",
        position: "top-right",
      });
    } finally {
      setInfoReqSubmitting(false);
    }
  };

  const requestDetailsTitle = "Request Details";
  /*statusKey === "active"
        ? "Modify Request Details"
        : statusKey === "inactive"
            ? "Rehire Request Details"
            : "New Request Details"; */

  if (loading) {
    return (
      <AppShell>
        <div className="ap-page-wrap page-scroll-scope">
          <div className="ap-gutter">
            <div className="ap-skeleton" style={{ maxWidth: 960 }}>
              <span className="skel w-60" />
              <span className="skel w-40" />
              <span className="skel w-60" />
              <span className="skel w-40" />
            </div>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <ToastContainer position="top-right" newestOnTop closeOnClick />
      <div className="ap-page-wrap page-scroll-scope" ref={scopeRef}>
        <div className="ap-gutter">
          {/* Title + Actions */}
          <div
            className="mr-titleblock"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
              marginBottom: 12,
            }}
          >
            <div>
              <button
                className="btn btn-back btn-slim"
                onClick={handleBack}
                disabled={sending}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    d="M15 18l-6-6 6-6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Back</span>
              </button>
            </div>

            <div style={{ flex: 1, textAlign: "center", minWidth: 0 }}>
              <div className="mr-title">{requestDetailsTitle}</div>
              {/* TODO:pentest */}
              {loadingDetailFails ? (
                <div className="mr-sub" style={{ color: "red" }}>
                  Failed to load request details.
                </div>
              ) : (
                <div className="mr-sub">
                  Read-only view. You can send a reminder if needed.
                </div>
              )}
              {/* <div className="mr-sub">Read-only view. You can send a reminder if needed.</div> */}
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button
                className="btn btn-slim"
                onClick={() => openEditPage()}
                title="Edit this request"
                /*TODO:pentest*/ disabled={
                  sending ||
                  statusKey === "rejected" ||
                  isFailedStatus ||
                  loadingDetailFails
                }
                // disabled={sending || statusKey === "rejected" || isFailedStatus}
              >
                Edit
              </button>
              <button
                className="btn btn-slim"
                onClick={openOffboardModal}
                disabled={!canOffboard}
                style={{ cursor: !canOffboard ? "not-allowed" : "pointer" }}
                title={
                  !canOffboard
                    ? isFteNominator && !hasAttuidForOffboard
                      ? "Offboarding is disabled because ATTUID is missing for this worker."
                      : "Offboarding is enabled only for Active workers."
                    : "Off Board Worker"
                }
              >
                Off Board Worker
              </button>
              {showReactivateAttuidButton && (
                <button
                  className="btn btn-slim"
                  onClick={onReactivateAttuidClick}
                  title={
                    disableReactivateAttuid
                      ? reactivateDisableWhy
                      : "Click to Reactivate ATTUID"
                  }
                  /*TODO:pentest*/ disabled={
                    disableReactivateAttuid || loadingDetailFails
                  }
                  // disabled={disableReactivateAttuid}
                  style={reactivateAttuidButtonStyle}
                >
                  Reactivate ATTUID
                </button>
              )}
              <button
                className="btn btn-remind btn-slim"
                onClick={sendReminder}
                /*TODO:pentest*/ disabled={
                  sending ||
                  reminderBlocked ||
                  loadingDetailFails ||
                  cpEmailDisabled
                }
                // disabled={sending || reminderBlocked}
                title={
                  cpEmailDisabled
                    ? "CP email is disabled"
                    : reminderBlocked
                      ? "Reminders are enabled only for Submitted / Submitted to CP / Offboarding Submitted / Offboarding Submitted to CP."
                      : undefined
                }
              >
                {sending ? "Sending…" : "Send Reminder"}
              </button>
            </div>
          </div>

          {/* Flow rail */}
          <div className="ap-flow ap-flow-rail flow-mini" ref={gridRef}>
            <Step
              title="Submitted By"
              lane={{ approved: true }}
              name={requestedBy}
              when={fmtDateLocal(requestedOn)}
              status={{ cls: "pill pill-blue", txt: "Submitted" }}
            />

            {(!truthy(data?.__is_lookup) ||
              truthy(data?.__lookup_sponsor_ok)) &&
              (flow === "sponsor-only" ||
                flow === "two-level" ||
                sponsorChoseNo) && (
                <Step
                  title={sponsorVM.title}
                  lane={sponsorVM.lane}
                  name={sponsorVM.name}
                  when={fmtDateLocal(sponsorVM.when)}
                />
              )}

            {cpTile && (
              <Step
                title="CP Delegate"
                lane={cpTile.lane}
                name={cpTile.name}
                when={fmtDateLocal(cpTile.when)}
                note={cpTile.note}
              />
            )}

            <div className="ap-step" style={finalTint}>
              <div className="step-body">
                <div className="title">Final Step</div>
                <span
                  className={`pill ${
                    final.label === "Approved"
                      ? "pill-ok"
                      : final.label === "Rejected"
                        ? "pill-bad"
                        : final.label === "Info Requested"
                          ? "pill-warm"
                          : "pill-blue"
                  }`}
                  style={{ marginBottom: 4 }}
                >
                  {final.label}
                </span>
                {final.note && (
                  <div className="meta" style={{ color: "#6b7280" }}>
                    {final.note}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Header line */}
          <div className="ap-hdrline ap-hdrline-tight">
            <div className="ap-hpair">
              <span className="lbl">{secIdLabel} #</span>
              <strong className="val pill pill-blue">
                {maskID(data?.security_id)}
              </strong>
            </div>
            <div className="ap-hpair">
              <span className="lbl">DTVID Status</span>
              <span className={`pill ${statusPillClass(rawDtvStatus)}`}>
                {show(dtvidStatusText)}
              </span>
            </div>

            <div className="ap-hpair">
              <span className="lbl">ATTUID Status</span>
              <span className={`pill ${statusPillClass(rawAttuidStatus)}`}>
                {show(attuidStatusText)}
              </span>
            </div>

            {showFailureDetails ? (
              <div className="ap-hpair">
                <span className="lbl">Failure Details</span>
                <span className="pill pill-bad" title={failureDetails}>
                  {show(failureDetails)}
                </span>
              </div>
            ) : null}
          </div>

          {/* Details grid */}
          <div className="ap-grid ap-grid-tight ap-grid-compact ap-grid-match">
            {/* Left */}
            <div className="ap-card ap-card-compact ap-fill">
              <div className="ap-card-head">Personal Details</div>
              <div className="ap-row">
                <span>First Name</span>
                <b>{show(data?.first_name)}</b>
              </div>
              <div className="ap-row">
                <span>Last Name</span>
                <b>{show(data?.last_name)}</b>
              </div>

              <div className="ap-row">
                <span>Email</span>
                <b className="break">{show(data?.email)}</b>
              </div>
              <div className="ap-row">
                <span>Phone</span>
                <b>{show(data?.phone)}</b>
              </div>
              <div className="ap-row">
                <span>Country of Citizenship</span>
                <b>{show(data?.citizenship)}</b>
              </div>

              {showDtvIdRow && (
                <div className="ap-row">
                  <span>DTVID</span>
                  <b className="break">{show(data?.dtv_id)}</b>
                </div>
              )}
              {showAttuidRow && (
                <div className="ap-row">
                  <span>ATTUID</span>
                  <b className="break">{show(data?.attuid)}</b>
                </div>
              )}

              {!hideFteSections && (
                <>
                  <div className="ap-card-head mt-10">Project Details</div>
                  <div className="ap-row">
                    <span>Supplier Reference</span>
                    <b>
                      {show(
                        data?.supplier_reference ||
                          data?.contract ||
                          data?.contract_po,
                      )}
                    </b>
                  </div>
                  <div className="ap-row">
                    <span>Vendor</span>
                    <b>
                      {show(
                        data?.vendor ||
                          data?.npw_manager_company ||
                          data?.supplier_name,
                      )}
                    </b>
                  </div>
                </>
              )}
            </div>

            {/* Middle */}
            <div className="ap-card ap-card-compact ap-fill">
              <div className="ap-card-head">Sponsorship &amp; Role</div>

              {!hideFteSections && (
                <>
                  <div className="ap-row">
                    <span>DTV Sponsor DTVUID</span>
                    <b className="break">{show(data?.sponsor)}</b>
                  </div>
                  <div className="ap-row">
                    <span>Role</span>
                    <b className="break">{show(data?.role)}</b>
                  </div>
                </>
              )}

              <div className="ap-row">
                <span>Webphone Manager DTVUID</span>
                <b className="break">{show(data?.webphone_manager)}</b>
              </div>
              {shouldShowAttuidSupervisor(
                rawAttuidStatus,
                data?.attuidSupervisor,
              ) && (
                <div className="ap-row">
                  <span>AT&T Supervisor ID</span>
                  <b className="break">{show(data?.attuidSupervisor)}</b>
                </div>
              )}
              <div className="ap-row">
                <span>ATT Role CP Role</span>
                <b>{show(data?.att_role_cp_role_desc)}</b>
              </div>
              <div className="ap-row">
                <span>Time Zone</span>
                <b className="break">{show(data?.time_zone)}</b>
              </div>

              <div className="ap-row">
                <span>City</span>
                <b className="break">{show(data?.city)}</b>
              </div>
              <div className="ap-row">
                <span>State</span>
                <b className="break">{show(data?.state)}</b>
              </div>
              <div className="ap-row">
                <span>Country</span>
                <b className="break">{show(data?.country)}</b>
              </div>

              <div className="ap-row">
                <span>Occupancy</span>
                <b>{show(data?.occupancy)}</b>
              </div>
              <div className="ap-row">
                <span>Employment Type</span>
                <b>{show(data?.employment_type)}</b>
              </div>
              <div className="ap-row">
                <span>ATTUID Required</span>
                <b>{attuidRequired ? "Yes" : "No"}</b>
              </div>

              <div className="ap-row">
                <span>Billable</span>
                <b>{show(data?.non_billable)}</b>
              </div>

              <div className="ap-card-head mt-10">
                Contact &amp; Assignment Details
              </div>

              {!hideFteSections && (
                <>
                  <div className="ap-row">
                    <span>Contract #</span>
                    <b>{show(data?.contract || data?.contract_po)}</b>
                  </div>
                  <div className="ap-row">
                    <span>PO #</span>
                    <b>{show(data?.po_number || data?.po_num)}</b>
                  </div>
                  <div className="ap-row">
                    <span>Line #</span>
                    <b>{show(data?.line_number)}</b>
                  </div>
                </>
              )}

              <div className="ap-row">
                <span>Start Date</span>
                <b>
                  {formatDateMMDDYYYY(data?.start_date) ||
                    show(data?.start_date)}
                </b>
              </div>
              <div className="ap-row">
                <span>End Date</span>
                <b>
                  {formatDateMMDDYYYY(data?.end_date) || show(data?.end_date)}
                </b>
              </div>
            </div>

            {/* Right — Approver Comments */}
            <div className="ap-panel ap-panel-compact ap-fill">
              <div className="ap-panel-title">Approver Comments</div>

              <div
                className="ap-comments"
                style={{ flex: "0 0 auto", height: "auto", minHeight: 44 }}
              >
                {(comments || []).length === 0 ? (
                  <div className="ap-comment none">No comments yet.</div>
                ) : (
                  comments
                    .slice()
                    .reverse()
                    .map((c, i) => (
                      <div
                        className="ap-comment"
                        key={`${i}-${c.source}-${String(c.text || "").slice(0, 24)}`}
                      >
                        <div className="text">{cleanCommentText(c.text)}</div>
                      </div>
                    ))
                )}
                {canSubmitInfoReqToCp && (
                  <>
                    <div className="ap-panel-title">Your Comments</div>

                    <div style={{ display: "grid", gap: 8, marginBottom: 10 }}>
                      <textarea
                        value={infoReqComment}
                        onChange={(e) =>
                          setInfoReqComment(
                            String(e.target.value || "").slice(
                              0,
                              INFOREQ_COMMENT_MAX_LENGTH,
                            ),
                          )
                        }
                        placeholder="Enter comment"
                        rows={4}
                        maxLength={INFOREQ_COMMENT_MAX_LENGTH}
                        className="ap-textarea ap-textarea-lg"
                        // style={{ width: "100%", resize: "vertical" }}
                        disabled={infoReqSubmitting}
                      />
                      <div
                        style={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <button
                          className="btn btn-slim"
                          type="button"
                          onClick={submitInfoRequestedToCp}
                          disabled={infoReqSubmitting || !infoReqCommentTrimmed}
                          title={
                            !infoReqCommentTrimmed
                              ? "Comment is required"
                              : "Submit comment to CP"
                          }
                        >
                          {infoReqSubmitting ? "Submitting…" : "Submit"}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="rd-spacer" aria-hidden />
        </div>
        <div className="rd-spacer" aria-hidden />
      </div>

      <ReactivateAttuidModal
        open={reactivateOpen}
        titleId="reactivate-attuid-title"
        lookupPayload={lookupSource}
        workerName={reactivateWorkerName}
        workerDtvId={reactivateWorkerDtvId}
        workerAttuid={reactivateWorkerAttuid}
        secLabel={reactivateSecLabel}
        confirmLabel={reactivateConfirmLabel}
        lookupSecExist={reactivateLookupSecExist}
        securityEditEnabled={reactivateSecurityEditEnabled}
        showSecurityEditButton={showSecurityEditButton}
        showSecurityDisableEditButton={showSecurityDisableEditButton}
        onEnableSecurityEdit={handleEnableReactivateSecurityEdit}
        onDisableSecurityEdit={handleDisableReactivateSecurityEdit}
        securityInputValue={reactivateSecurityInputValue}
        confirmInputValue={reactivateConfirmInputValue}
        securityMaskedValue={reactivateSecurityMaskedValue}
        confirmMaskedValue={reactivateConfirmMaskedValue}
        showSecurity={reactivateShowSecurity}
        showConfirmSecurity={reactivateShowConfirmSecurity}
        onToggleShowSecurity={() => setReactivateShowSecurity((v) => !v)}
        onToggleShowConfirmSecurity={() =>
          setReactivateShowConfirmSecurity((v) => !v)
        }
        securityLocked={reactivateSecurityLocked}
        isUS={reactivateIsUS}
        submitting={reactivateSubmitting}
        onSecurityChange={(value) =>
          onReactivateSecurityChange(value, "security")
        }
        onConfirmSecurityChange={(value) =>
          onReactivateSecurityChange(value, "confirm")
        }
        errors={reactivateErrors}
        associationStatus={reactivateAssociation?.status}
        associationMessage={reactivateAssociation?.message}
        missingCriticalFields={missingCriticalFields}
        onCancel={() => {
          if (reactivateSubmitting) return;
          setReactivateOpen(false);
          resetReactivateForm();
        }}
        onValidate={
          requiresReactivateCpValidation ? validateReactivateAttuid : null
        }
        onSubmit={submitReactivateAttuid}
        validating={reactivateValidating}
        validateDisabled={reactivateValidateDisabled}
        validateStatus={reactivateCpValidation?.status}
        validateMessage={reactivateCpValidation?.message}
        submitDisabled={reactivateSubmitDisabled}
        submitMessage={reactivateSubmitValidationMessage}
        loading={reactivateLoading}
        validateButtonStyle={{
          background: reactivateValidateDisabled
            ? "linear-gradient(135deg, #94a3b8 0%, #cbd5e1 100%)"
            : "linear-gradient(135deg, #0f766e 0%, #1d4ed8 100%)",
          color: "#ffffff",
          borderColor: reactivateValidateDisabled ? "#94a3b8" : "#1d4ed8",
          boxShadow: reactivateValidateDisabled
            ? "none"
            : "0 12px 24px rgba(29, 78, 216, 0.28)",
        }}
      />

      <OffboardWorkerModal
        open={isOffboardModalOpen}
        titleId="offboard-title-rd"
        workerName={workerName}
        endDate={offboardEndDate}
        onEndDateChange={(value) => {
          setOffboardEndDate(value);
          setOffboardDateError("");
        }}
        offboardIdType={offboardIdType}
        onOffboardIdTypeChange={setOffboardIdType}
        canUseAttuid={hasAttuidForOffboard}
        disableDtvOption={isFteNominator}
        note={offboardNote}
        onNoteChange={setOffboardNote}
        dateError={offBoardDateError}
        error={offBoardError}
        submitting={offboarding}
        canSubmit={canOffboard}
        onCancel={() => {
          if (offboarding) return;
          setIsOffboardModalOpen(false);
          setOffboardDateError("");
          setOffboardError("");
        }}
        onSubmit={submitOffboard}
      />
    </AppShell>
  );
}
